import {
  AlertColor,
  Box,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Tooltip,
  autocompleteClasses,
} from '@mui/material';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import TitleText from '../../components/formlib/TitleText';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ReactComponent as DPHLogo } from '../../assets/images/dphLogo.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/infoIcon.svg';
import { ReactComponent as AddNew } from '../../assets/images/Plus.svg';
import { ReactComponent as Lock } from '../../assets/images/locked.svg';
import { ReactComponent as File } from '../../assets/images/file.svg';
import SingleLine from '../../components/formlib/SingleLine';
import CustomRadioButton from '../../components/formlib/CustomRadioButton';
import DropdownComponent from '../../components/formlib/DropdownComponent';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';

import InformationModal from './InformationModal';
import ServiceDeliveryPlan from './ServiceDeliveryPlan';
import HealthEligibilitySummary from './HealthEligibilitySummary';
import './IFSP.css';
import DevelopmentalSummary from './DevelopmentalSummary';
import FamiliyConcernsPrioritiesandResources from './FamilyConcernsPrioritiesandResources';
import ChildandFamilyIFSPOutcomes from './ChildandFamilyIFSPOutcomes';
import TransitionPreparation from './TransitionPreparation';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import DailyRoutine from './DailyRoutine';
import TransitionPlan from './TransitionPlan';
import ConsentChangeServiceDeliveryPlan from './ConsentChangeServiceDeliveryPlan';
import IfspContext, {
  IfspContextDataType,
  IfspTeamMembersType,
} from './ifspContextApi/IfspContex';
import AutocompleteSearch from '../../components/formlib/AutocompleteSearch';
import { OptionType, employeeRoles } from '../../utils/type';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import dayjs, { Dayjs } from 'dayjs';
import {
  EmployeeListType,
  createIfsp,
  downloadIfsp,
} from '../../services/ifspClient/IfspApi';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import { Formik, FormikProps } from 'formik';
import * as yup from 'yup';
import ModalPopup from '../../components/formlib/ModalPopup';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import { FACESHEET } from '../../routes/Routing';
import CustomBackArrow from '../../components/imagepickers/backArrow';
import { useLocation, useNavigate } from 'react-router';
import IfspTeamMembersTableRow from './IfspTeamMemberTableRow';
import { convertGenderToFrontend } from '../../utils/genderUtil';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import { datePickerMinDateMaxDateValidate } from '../../utils/dateUtil';
import {
  LookUpContext,
  LookUpContextType,
} from '../../context/LookUpContextProvider';
import IfspUpdateModalPopup from './IfspUpdateModalPopup';
import IfspReviewedOnListModal from './IfspReviewedOnListModal';
import Textinput from '../../components/formlib/Textinput';
import { checkOffline } from '../../services/Offline';
import ShareDownloadModal from '../../components/formlib/modal/ShareDownloadModal';
import { v4 as uuidv4 } from 'uuid';
import TranslateWidget, {
  isOtherLanguage,
  setLanguage,
} from '../../TranslateWidget';
import EmptyScreen from '../../components/shared/EmptyScreen';
import { formatName } from '../../utils/nameUtils';
import CommentTextbox from '../../components/formlib/CommentTextbox';
import { UnSavedChangesContext } from '../../context/UnSavedChangesProvider';
import ProgramPanel from '../../components/forms/ProgramPanel';
import { useMultiplePermissions } from '../../hooks/usePermissions';
import { REFERRAL_STATUS_KEY } from '../../services/Constant';
import LazyAutocompleteSearch from '../../components/formlib/LazyAutocompleteSearch';
import { getAllEmployeeByName } from '../../services/configApi/employees/employeeServices';
import { shouldForceDisableOldIfsp } from '../../utils/utilities';
import { getClientDocumentFileName } from '../../utils/fileUtils';

const info =
  'The Individualized Family Service Plan (IFSP) is a working document that outlines the Early Intervention (EI) services to be provided. The initial IFSP (based on a timely and comprehensive multidisciplinary evaluation and assessment) is completed within 45 days of referral. The plan is developed collaboratively among IFSP Team Members - including parents, caregivers, EI staff, Specialty Services Provider (SSP) staff and/or others, as needed. Participants in the development of the IFSP may also include community representatives, extended family members, and others as requested by the parent if feasible to do so. The EI Service Coordinator is responsible for implementing the plan, preparing for ongoing IFSP meetings, and meeting state and federal timelines.';

type DateOrNull = string | Dayjs | null;

interface TableData {
  name: string;
  role: string;
  discipline: string;
  email: string;
  phoneNumber: string;
  jobDescription: string;
  startDate: string;
  endDate: string;
  actions: string;
}
const headCells: HeadCell<TableData>[] = [
  {
    id: 'name',
    labelId: 'IFSP.Name',
    defaultLabelId: 'Name',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'discipline',
    labelId: 'IFSP.DisciplineText',
    defaultLabelId: 'Discipline',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'role',
    labelId: 'IFSP.RoleText',
    defaultLabelId: 'Role',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'email',
    labelId: 'IFSP.EmailText',
    defaultLabelId: 'Email',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'phoneNumber',
    labelId: 'IFSP.PhoneNumberText',
    defaultLabelId: 'Phone Number',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'startDate',
    labelId: 'IFSP.StartDateText',
    defaultLabelId: 'Start Date',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'endDate',
    labelId: 'IFSP.EndDateText',
    defaultLabelId: 'End Date',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'actions',
    labelId: 'IFSP.ActionsText',
    defaultLabelId: 'Actions',
    numeric: false,
    requiredSorting: false,
  },
];

export const IfspRadioData = [
  {
    value: 'parent',
    labelId: 'IFSP.GuardianText',
    defaultLabel: 'Guardian',
  },
  {
    value: 'teamMember',
    labelId: 'IFSP.TeamMemberText',
    defaultLabel: 'Team Member',
  },
  {
    value: 'other',
    labelId: 'IFSP.OtherText',
    defaultLabel: 'Others',
  },
];

const validationSchema = yup.object().shape({
  contactType: yup.string(),
  yetToAddContactId: yup.string().when('contactType', {
    is: 'parent',
    then: yup.string().required('IFSP.yetToAddContactId.validation'),
    otherwise: yup.string().nullable(),
  }),
  yetToAddOtherContactId: yup.string().when('contactType', {
    is: 'teamMember',
    then: yup.string().required('IFSP.yetToAddOtherContactId.validation'),
    otherwise: yup.string().nullable(),
  }),
  yetToAddRoleId: yup.string().when('contactType', {
    is: 'teamMember',
    then: yup.string().required('IFSP.yetToAddRoleId.validation'),
    otherwise: yup.string().nullable(),
  }),
  yetToAddDisciplineId: yup.string().when('contactType', {
    is: 'teamMember',
    then: yup.string().required('IFSP.yetToAddDisciplineId.validation'),
    otherwise: yup.string().nullable(),
  }),
  teamMemberStartDate: yup.string().when('contactType', {
    is: 'teamMember',
    then: yup.string().required('IFSP.teamMemberStartDate.validation'),
    otherwise: yup.string().nullable(),
  }),
  teamMemberEndDate: yup.string().nullable(),
  othersName: yup.string().when('contactType', {
    is: 'other',
    then: yup.string().required('IFSP.othersName.validation'),
    otherwise: yup.string().nullable(),
  }),
  othersJobDescription: yup.string().when('contactType', {
    is: 'other',
    then: yup.string().notRequired().nullable(),
    otherwise: yup.string().nullable(),
  }),
  othersStartDate: yup.string().when('contactType', {
    is: 'other',
    then: yup.string().required('IFSP.othersStartDate.validation'),
    otherwise: yup.string().nullable(),
  }),
  othersEndDate: yup.string().nullable(),
});
const validateDate = (values: {
  contactType: string;
  yetToAddContactId: string;
  yetToAddOtherContactId: string;
  yetToAddRoleId: string;
  teamMemberStartDate: DateOrNull;
  teamMemberEndDate: DateOrNull;
  othersStartDate: DateOrNull;
  othersEndDate: DateOrNull;
}) => {
  const errors: Partial<{
    contactType: string;
    yetToAddContactId: string;
    yetToAddOtherContactId: string;
    yetToAddRoleId: string;
    teamMemberStartDate: string | null;
    teamMemberEndDate: string | null;
    othersStartDate: string | null;
    othersEndDate: string | null;
  }> = {};

  if (values.teamMemberStartDate && values.teamMemberEndDate) {
    const startDate = dayjs(values.teamMemberStartDate, 'MM/DD/YYYY');
    const endDate = dayjs(values.teamMemberEndDate, 'MM/DD/YYYY');

    if (startDate.isAfter(endDate)) {
      errors.teamMemberEndDate = 'IFSP.endDateValidationError';
    }
  }
  if (datePickerMinDateMaxDateValidate(values.teamMemberStartDate)) {
    errors.teamMemberStartDate = 'datePickerMinDateMaxDateValidate';
  }

  if (datePickerMinDateMaxDateValidate(values.teamMemberEndDate)) {
    errors.teamMemberEndDate = 'datePickerMinDateMaxDateValidate';
  }

  if (values.othersStartDate && values.othersEndDate) {
    const startDate = dayjs(values.othersStartDate, 'MM/DD/YYYY');
    const endDate = dayjs(values.othersEndDate, 'MM/DD/YYYY');
    if (startDate.isAfter(endDate)) {
      errors.othersEndDate = 'IFSP.endDateValidationError';
    }
  }
  if (datePickerMinDateMaxDateValidate(values.othersStartDate)) {
    errors.othersStartDate = 'datePickerMinDateMaxDateValidate';
  }

  if (datePickerMinDateMaxDateValidate(values.othersEndDate)) {
    errors.othersEndDate = 'datePickerMinDateMaxDateValidate';
  }
  return errors;
};

const IFSPScreen = () => {
  const {
    ifsp,
    employeeData,
    getIfsp,
    updateIfspContext,
    updateIfspTouched,
    updateOutComesFieldFlag,
    updateOutComesEndDateFlag,
    updateOutComesStartDateFlag,
    ifspTouched,
    isTransitionDateReqired,
    isLeaReferralDateRequired,
    isTpcMeetingDateRequired,
    tpcMeetingDateOutOfRange,
    leaReferralDateOutOfRange,
    transitionDateOutOfRange,
    isOptOutNotificationDateRequired,
    primarySettingCode,
    reasonData,
    activeReasonData,
    isDailyRoutineUpdated,
    isDevelopmentSummaryUpdated,
    isFamilyConcernsUpdated,
    isHealthAndEligibilityUpdated,
  } = useContext(IfspContext);
  const navigation = useNavigate();
  const { state } = useLocation();
  const [isIfspDueDatePassed, setIsIfspDueDatePassed] =
    useState<boolean>(false);
  const [reasonForDelay, setReasonForDelay] = useState<OptionType>(
    ifsp.reasonForDelay,
  );
  const [openDelayModal, setOpenDelayModal] = useState<boolean>(false);
  const [showAlertDialog, setShowAlertDialog] = useState<boolean>(false);
  const [infoModal, setInfoModal] = useState(false);
  const [contactType, setContactType] = useState('parent');
  const [closeDailyRoutine, setCloseDailyRoutine] = useState<boolean>(false);
  const [closeHealthEligibility, setCloseHealthEligibility] = useState(false);
  const [closeDevelopmentSummary, setCloseDevelopmentSummary] = useState(false);
  const [closeConsentChangeSDP, setCloseConsentChangeSDP] = useState(false);
  const [closeFamilyConcern, setCloseFamilyConcern] = useState<boolean>(false);
  const [openTransitionModal, setOpenTransitionModal] =
    useState<boolean>(false);
  const [openSDP, setOpenSDP] = useState(false);

  const [openOutCome, setOpenOutCome] = useState<boolean>(false);
  const [openTransistionPrep, setOpenTransistionPrep] =
    useState<boolean>(false);
  const [infoDailyRoutineModal, setInfoDailyRoutineModal] = useState(false);
  const [infoTransitionPlanModal, setInfoTransitionPlanModal] =
    useState<boolean>(false);
  const [infoConsentChangeSDPModal, setInfoConsentChangeSDPModal] =
    useState<boolean>(false);
  const [infoSDPModal, setInfoSDPModal] = useState<boolean>(false);
  const [infoHESModal, setInfoHESModal] = useState<boolean>(false);
  const [infoOutComeModal, setInfoOutComeModal] = useState<boolean>(false);
  const [infoReviewModal, setInfoReviewModal] = useState<boolean>(false);
  const [infoTransistionPrepModal, setInfoTransistionPrepModal] =
    useState<boolean>(false);
  const [infoDevelopmentalSummaryModal, setInfoDevelopmentalSummaryModal] =
    useState<boolean>(false);
  const [infoFamilyConcernsModal, setInfoFamilyConcernsModal] =
    useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openUpdateModal, setOpenUpdateModal] = useState<boolean>(false);
  const [openIfspReviewedOnListModal, setOpenIfspReviewedOnListModal] =
    useState<boolean>(false);
  const [
    openIfspReviewedOnListClickedIndex,
    setOpenIfspReviewedOnListClickedIndex,
  ] = useState<number>(0);
  const [shareModal, setShareModal] = useState<boolean>(false);

  const [employeeList, setEmployeeList] = useState<EmployeeListType[]>([
    {
      id: '',
      name: '',
      firstName: '',
      lastName: '',
      email: '',
      disciplineType: '',
      phone: '',
      roles: [],
    },
  ]);

  const [employeeListForTeam, setEmployeeListForTeam] = useState<
    EmployeeListType[]
  >([
    {
      id: '',
      name: '',
      firstName: '',
      lastName: '',
      email: '',
      disciplineType: '',
      phone: '',
      roles: [],
    },
  ]);

  const [parentContactes, setParentContactes] = useState<OptionType[]>([]);
  const [teamMembers, setTeamMembers] = useState<OptionType[]>([]);
  const [teamMembersRole, setTeamMembersRole] = useState<OptionType[]>([]);
  const [reportCompletedByRole, setReportCompletedByRole] = useState<
    OptionType[]
  >([]);
  const [teamMembersDiscipline, setTeamMembersDiscipline] = useState<
    OptionType[]
  >([]);
  const [teamMembersStartDate, setTeamMembersStartDate] =
    useState<DateOrNull>(null);
  const [teamMembersEndDate, setTeamMembersEndDate] =
    useState<DateOrNull>(null);

  const [ifspTeamTable, setIfspTeamTable] = useState<IfspTeamMembersType[]>([]);
  const [yetToAddContactId, setYetToAddContactId] = useState<string>('');
  const [yetToAddOtherContactId, setYetToAddOtherContactId] =
    useState<string>('');
  const [yetToAddRoleId, setYetToAddRoleId] = useState<string>('');
  const [yetToAddDisciplineId, setYetToAddDisciplineId] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [othersName, setOthersName] = useState<string>('');
  const [othersJobDescription, setOthersJobDescription] = useState<string>('');
  const [othersStartDate, setOthersStartDate] = useState<DateOrNull>(null);
  const [othersEndDate, setOthersEndDate] = useState<DateOrNull>(null);
  const [editRecordIndex, setEditRecordIndex] = useState<number | null>(null);
  const [editOtherRecordIndex, setEditOtherRecordIndex] = useState<
    number | null
  >(null);
  const [deleteIndex, setDeleteIndex] = useState<number>(0);
  const [deleteWarning, setDeleteWarning] = useState<boolean>(false);
  const [completedByBackup, setCompletedByBackup] = useState<OptionType>({
    id: ifsp.completedBy.id,
    label: ifsp.completedBy.label,
  });
  const [isUserManuallyAddedReportedBy, setIsUserManuallyAddedReportedBy] =
    useState<boolean>(false);

  const [reportedCompletedBy, setReportedCompletedBy] =
    useState<OptionType | null>({
      id: '',
      label: '',
    });
  const [open, setOpen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');

  const [ifspScreenFieldLock, setIfspScreenFieldLock] =
    useState<boolean>(false);

  const [isreportCompletedByMandatory, setIsreportCompletedByMandatory] =
    useState<boolean>(false);
  const [isreportCompletedRoleMandatory, setIsreportCompletedRoleMandatory] =
    useState<boolean>(false);
  const [reportCompletedByRoleId, setReportCompletedByRoleId] =
    useState<string>('');

  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const [offlineMode, setOfflineMode] = React.useState(false);
  const [saveClicked, setSaveClicked] = useState<boolean>(false);
  const [isErrorOccuredInOutCome, setIsErrorOccuredInOutCome] =
    React.useState(false);
  const [isErrorOccuredInOutComeEndDate, setIsErrorOccuredInOutComeEndDate] =
    useState<boolean>(false);
  const [
    isErrorOccuredInOutComeStartDate,
    setIsErrorOccuredInOutComeStartDate,
  ] = useState<boolean>(false);
  const { hasUnsavedChanges, handleUpdateUnsavedChanges } = useContext(
    UnSavedChangesContext,
  );
  const [openUnSavedModal, setOpenUnSavedModal] = useState<boolean>(false);
  React.useLayoutEffect(() => {
    const getOfflineMode = async () => {
      const offlineFlag = await checkOffline();
      setOfflineMode(offlineFlag);
    };
    getOfflineMode();
  });

  const latestConsentForChange =
    ifsp.ifspTeam.forms.consentForChangeForSDP[
      ifsp.ifspTeam.forms.consentForChangeForSDP.length - 1
    ];

  const hasConsentSignature =
    latestConsentForChange.consentSignature.some(
      (x) => x.signature || x.rowKey || x.verbalConsent,
    ) ||
    (latestConsentForChange.isConsentSkipped &&
      latestConsentForChange.employeeSignature.signature);
  const hasParentSignature =
    latestConsentForChange.parentSignature.some(
      (x) => x.signature || x.rowKey || x.verbalConsent,
    ) ||
    (latestConsentForChange.isConsentSkipped &&
      latestConsentForChange.employeeSignature.signature);
  const hasChildOutcomes = ifsp.ifspTeam.forms.childIfspOutcomes.some(
    (x) => x.outcomesProgressReview.startDate,
  );
  const hasServiceDeliveryPlan = ifsp.ifspTeam.forms.serviceDeliveryPlan.some(
    (x) => x.earlyInterventionService.length > 0,
  );

  const isLatestConsentSigned = hasConsentSignature && hasParentSignature;
  const [canEditAfterLock, setCanEditAfterLock] =
    React.useState<boolean>(false);
  const [canDeleteAfterLock, setCanDeleteAfterLock] =
    React.useState<boolean>(false);
  const [isFromClientJourny, setIsFromClientJourny] =
    React.useState<boolean>(false);
  const [selectedLang, setSelectedLang] = useState<string>(
    ifsp.selectedLanguage,
  );
  const [isIfspInitiated, setIsIfspInitiated] = useState<boolean>(!!ifsp.id);
  const [refresh, setRefresh] = useState(0);
  const [isCancelClicked, setIsCancelClicked] = useState<boolean>(false);
  const { lookups } = useContext(LookUpContext) as LookUpContextType;
  const checkAndSetIfspScreenFieldLock = (ifspData: IfspContextDataType) => {
    const { consentForChangeForSDP } = ifspData.ifspTeam.forms;
    const latestConsent =
      consentForChangeForSDP[consentForChangeForSDP.length - 1];

    const isEmptySignature = (
      signatures: {
        id: string;
        isUpdated: boolean;
        signature: string;
        date: DateOrNull;
        partitionKey: string;
        rowKey: string;
        verbalConsent: boolean;
      }[],
    ) =>
      signatures.every(
        (sig) => !sig.rowKey && !sig.signature && !sig.verbalConsent,
      );

    const hasEmptyParentSignature = isEmptySignature(
      latestConsent.parentSignature,
    );
    const hasEmptyConsentSignature = isEmptySignature(
      latestConsent.consentSignature,
    );

    if (hasEmptyParentSignature && hasEmptyConsentSignature) {
      setIfspScreenFieldLock(false);
    } else if (
      !hasEmptyParentSignature &&
      !hasEmptyConsentSignature &&
      ifsp.ifspTeam.forms.serviceDeliveryPlan.some(
        (x) => x.earlyInterventionService.length > 0,
      ) &&
      ifsp.ifspTeam.forms.childIfspOutcomes.some(
        (x) => x.outcomesProgressReview.startDate,
      )
    ) {
      setIfspScreenFieldLock(true);
    }
  };
  useEffect(() => {
    checkAndSetIfspScreenFieldLock(ifsp);
  }, [ifsp.selectedLanguage]);
  useEffect(() => {
    setLanguage(ifsp.selectedLanguage);
    setSelectedLang(ifsp.selectedLanguage);
  }, [ifsp.selectedLanguage]);
  useEffect(() => {
    setIsIfspInitiated(!!ifsp.id);
    fetchEmployees('', 0);
  }, [ifsp.id]);

  useEffect(() => {
    const teamMemberIds = ifsp.ifspTeam.teamMember
      .filter((x) => x.role)
      .map((x) => x.id);
    getAllEmployeeByName('', 0, 10, teamMemberIds)
      .then((data) => {
        setEmployeeListForTeam(data);

        toggleLoader(false);

        return data.map((emp) => ({
          id: emp.id,
          label: `${emp.lastName}, ${emp.firstName}`,
        }));
      })
      .catch((err) => {
        toggleLoader(false);
        setToastrVariable('error');
        setToastrId('IFSP.failedToGetEmployeeData');
        setToastrDefaultMessage(err);

        return [];
      });
  }, []);

  useEffect(() => {
    if (state?.openTransitionPlanning) {
      setIsFromClientJourny(state.openTransitionPlanning);
      setOpenTransitionModal(true);
      autoScrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    if (
      checkPermissionForFeature(
        'backend.update_ifsp_after_lock',
        'editPermission',
      ) &&
      !shouldForceDisableOldIfsp()
    ) {
      setCanEditAfterLock(true);
    } else {
      setCanEditAfterLock(false);
    }
    if (
      checkPermissionForFeature(
        'backend.update_ifsp_after_lock',
        'deletePermission',
      ) &&
      !shouldForceDisableOldIfsp()
    ) {
      setCanDeleteAfterLock(true);
    } else {
      setCanDeleteAfterLock(false);
    }
  }, []);
  const autoScrollRef = React.useRef<HTMLDivElement>(null);

  const permissions = useMultiplePermissions([
    'backend.clients',
    'backend.in_active_referrals',
  ]);
  const referralStatus = localStorage.getItem(REFERRAL_STATUS_KEY) ?? 'None';
  const hasEditPermission =
    referralStatus === 'Inactive'
      ? permissions['backend.clients'].canEdit &&
        permissions['backend.in_active_referrals'].canEdit &&
        !shouldForceDisableOldIfsp()
      : (permissions['backend.clients'].canEdit ||
          permissions['backend.in_active_referrals'].canEdit) &&
        !shouldForceDisableOldIfsp();

  useEffect(() => {
    if (!hasEditPermission) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [hasEditPermission]);

  const formikRef = useRef<
    FormikProps<{
      contactType: string;
      yetToAddContactId: string;
      yetToAddOtherContactId: string;
      yetToAddRoleId: string;
      yetToAddDisciplineId: string;
      teamMemberStartDate: DateOrNull;
      teamMemberEndDate: DateOrNull;
      othersName: string;
      othersJobDescription: string;
      othersStartDate: DateOrNull;
      othersEndDate: DateOrNull;
    }>
  >(null);

  const handleOpenInfoModal = () => {
    setInfoModal(true);
  };

  const handleCloseInfoModal = () => {
    setInfoModal(false);
  };

  const parentRef = useRef<HTMLDivElement>(null);

  const selectedMember = useMemo(() => {
    const found = teamMembers.find(
      (item) => item.id === formikRef.current?.values.yetToAddOtherContactId,
    );

    return found ?? null;
  }, [teamMembers, formikRef.current?.values.yetToAddOtherContactId]);

  const sanitizeSearch = (text: string) => {
    if (!text) return '';

    if (text.includes(',')) {
      const [last, first] = text.split(',');
      return `${first?.trim()} ${last?.trim()}`;
    }

    return text;
  };

  const fetchEmployees = (
    search: string,
    page: number,
  ): Promise<OptionType[]> => {
    if (formikRef.current?.values.contactType === 'teamMember' && page === 0) {
      toggleLoader(true);
    } else {
      toggleLoader(false);
    }
    const cleanSearch = search ? sanitizeSearch(search) : '';

    const teamMemberIds = ifsp.ifspTeam.teamMember
      .filter((x) => x.role)
      .map((x) => x.id);

    return getAllEmployeeByName(
      cleanSearch,
      page,
      10,
      page === 0 ? teamMemberIds : [],
    )
      .then((data) => {
        setEmployeeListForTeam((prev) =>
          page === 0 ? data : [...prev, ...data],
        );

        return data.map((emp) => ({
          id: emp.id,
          label: `${emp.lastName}, ${emp.firstName}`,
        }));
      })
      .catch((err) => {
        toggleLoader(false);
        setToastrVariable('error');
        setToastrId('IFSP.failedToGetEmployeeData');
        setToastrDefaultMessage(err);

        return [];
      })
      .finally(() => {
        setTimeout(() => {
          toggleLoader(false);
        }, 100);
      });
  };

  const handleAddNewContact = () => {
    const newContact = ifsp.ifspTeam.contact.find(
      (contact) => contact.id === yetToAddContactId,
    );
    if (newContact) {
      updateIfspContext({
        ...ifsp,
        ifspTeam: {
          ...ifsp.ifspTeam,
          parents: [
            ...ifsp.ifspTeam.parents,
            {
              id: newContact.id.toString(),
              email: newContact.email,
              name: newContact.name,
              phone: newContact.phone,
              role: newContact.relationshipToClient,
            },
          ],
        },
      });
    }
    formikRef.current?.resetForm();
    formikRef.current?.setFieldValue('contactType', 'parent');
    setTeamMembersRole([]);
    setTeamMembersDiscipline([]);
    setYetToAddContactId('');
    setYetToAddOtherContactId('');
    setYetToAddRoleId('');
    setYetToAddDisciplineId('');
  };
  const handleAddNewTeamMemberContact = () => {
    const newOtherContact = employeeListForTeam.find(
      (employee) => employee.id === yetToAddOtherContactId,
    );

    let extractedData;
    if (contactType === 'teamMember') {
      extractedData = ifspTeamTable.filter(
        (_, index) => index !== editRecordIndex,
      );
    } else if (contactType === 'other') {
      extractedData = ifspTeamTable.filter(
        (_, index) => index !== editOtherRecordIndex,
      );
    }

    // Check if there's any record in the table with the same email, role, and overlapping startDate/endDate
    const isDuplicated =
      extractedData &&
      extractedData.some((otherContact) => {
        const startDateprev = dayjs(otherContact.startDate, 'MM/DD/YYYY');
        const endDateprev = dayjs(otherContact.endDate, 'MM/DD/YYYY');
        const startDateNew = dayjs(
          formikRef.current?.values.teamMemberStartDate,
          'MM/DD/YYYY',
        );
        const endDateNew = dayjs(
          formikRef.current?.values.teamMemberEndDate,
          'MM/DD/YYYY',
        );

        const isOverlapping =
          (startDateprev.isBefore(endDateNew) &&
            endDateprev.isAfter(startDateNew)) ||
          startDateprev.isSame(startDateNew) ||
          endDateprev.isSame(endDateNew);

        return (
          otherContact.email === newOtherContact?.email &&
          (otherContact.role === role ||
            otherContact.discipline === yetToAddDisciplineId) &&
          isOverlapping
        );
      });

    if (editRecordIndex !== null && !isDuplicated) {
      const formattedEndDate =
        formikRef.current?.values.teamMemberEndDate ||
        (ifspTeamTable[editRecordIndex].endDate &&
        formikRef.current?.values.teamMemberEndDate
          ? dayjs(ifspTeamTable[editRecordIndex].endDate).format('MM/DD/YYYY')
          : '');
      const updatedRecord = {
        id: newOtherContact?.id ?? ifspTeamTable[editRecordIndex].id,
        email: newOtherContact?.email ?? ifspTeamTable[editRecordIndex].email,
        name:
          formatName(
            newOtherContact?.firstName ?? '',
            null,
            newOtherContact?.lastName ?? '',
            null,
          ) ?? ifspTeamTable[editRecordIndex].name,
        phone: newOtherContact?.phone ?? ifspTeamTable[editRecordIndex].phone,
        role: role ?? ifspTeamTable[editRecordIndex].role,
        discipline:
          formikRef.current?.values.yetToAddDisciplineId ??
          ifspTeamTable[editRecordIndex].discipline,
        startDate:
          formikRef.current?.values.teamMemberStartDate ??
          dayjs(ifspTeamTable[editRecordIndex].startDate).format('MM/DD/YYYY'),
        endDate: formattedEndDate,
      };

      setIfspTeamTable((prev) => {
        const updatedTable = [...prev];
        updatedTable[editRecordIndex] = updatedRecord;
        updateIfspContext({
          ...ifsp,
          ifspTeam: {
            ...ifsp.ifspTeam,
            teamMember: updatedTable.filter(
              (item) =>
                typeof item.id !== 'number' &&
                !item.jobDescription &&
                item.discipline,
            ),
          },
        });

        return updatedTable;
      });
    } else if (newOtherContact && !isDuplicated) {
      updateIfspContext({
        ...ifsp,
        ifspTeam: {
          ...ifsp.ifspTeam,
          teamMember: [
            ...ifsp.ifspTeam.teamMember,
            {
              id: newOtherContact.id || '',
              email: newOtherContact.email ?? '',
              name:
                formatName(
                  newOtherContact?.firstName ?? null,
                  null,
                  newOtherContact?.lastName ?? null,
                  null,
                ) ?? '',
              phone: newOtherContact.phone ?? '',
              role: role || '',
              discipline: formikRef.current?.values.yetToAddDisciplineId || '',
              startDate: formikRef.current?.values.teamMemberStartDate ?? '',
              endDate: formikRef.current?.values.teamMemberEndDate ?? '',
            },
          ],
        },
      });
    } else if (isDuplicated) {
      setShowAlertDialog(true);
    }
    setEditRecordIndex(null);
    setTeamMembersRole([]);
    setTeamMembersDiscipline([]);
    setTeamMembersStartDate('');
    setTeamMembersEndDate('');
    setYetToAddContactId('');
    setYetToAddOtherContactId('');
    setYetToAddRoleId('');
    setYetToAddDisciplineId('');
    formikRef.current?.resetForm();
    formikRef.current?.setValues({
      teamMemberEndDate: '',
      teamMemberStartDate: '',
      contactType: 'teamMember',
      yetToAddContactId: '',
      yetToAddOtherContactId: '',
      yetToAddRoleId: '',
      yetToAddDisciplineId: '',
      othersEndDate: '',
      othersStartDate: '',
      othersJobDescription: '',
      othersName: '',
    });
    formikRef.current?.setFieldValue('contactType', 'teamMember');
  };

  const handelAddOtherContact = () => {
    if (editOtherRecordIndex !== null) {
      const fallbackEndDate =
        ifspTeamTable[editOtherRecordIndex].endDate &&
        formikRef.current?.values.othersEndDate
          ? ifspTeamTable[editOtherRecordIndex].endDate
          : '';

      const formattedEndDate = formikRef.current?.values.othersEndDate
        ? formikRef.current?.values.othersEndDate
        : fallbackEndDate;

      const updatedRecord = {
        name:
          formikRef.current?.values.othersName ??
          ifspTeamTable[editOtherRecordIndex].name,
        jobDescription:
          formikRef.current?.values.othersJobDescription ??
          ifspTeamTable[editOtherRecordIndex].jobDescription ??
          '',
        startDate:
          formikRef.current?.values.othersStartDate ??
          ifspTeamTable[editOtherRecordIndex].startDate ??
          '',
        endDate: formattedEndDate,
        id: `${editOtherRecordIndex}`,
        email: '', // Assuming these fields should be empty
        phone: '', // Assuming these fields should be empty
        role: '', // Assuming these fields should be empty
        discipline: '', // Assuming these fields should be empty
      };

      setIfspTeamTable((prev) => {
        const updatedTable = [...prev];
        updatedTable[editOtherRecordIndex] = updatedRecord;

        updateIfspContext({
          ...ifsp,
          ifspTeam: {
            ...ifsp.ifspTeam,
            others: updatedTable
              .filter((item) => !item.discipline && item.startDate)
              .map((item) => ({
                ...item,
                name: item.name,
                jobDescription: item.jobDescription ?? '',
                startDate: item.startDate,
                endDate: item.endDate,
              })),
          },
        });

        return updatedTable;
      });
    } else {
      const newRecord = {
        id: `${ifspTeamTable.length}`,
        name: formikRef.current?.values.othersName ?? '',
        jobDescription: formikRef.current?.values.othersJobDescription ?? '',
        startDate: formikRef.current?.values.othersStartDate ?? '',
        endDate: formikRef.current?.values.othersEndDate ?? '',
        email: '',
        phone: '',
        role: '', // Adjust as needed
        discipline: '',
      };
      setIfspTeamTable((prev) => {
        return [...prev, newRecord];
      });
      updateIfspContext({
        ...ifsp,
        ifspTeam: {
          ...ifsp.ifspTeam,
          others: [...ifsp.ifspTeam.others, newRecord],
        },
      });
    }

    // Reset the form and state
    formikRef.current?.setValues({
      contactType: 'other',
      othersEndDate: '',
      othersJobDescription: '',
      othersName: '',
      othersStartDate: '',
      teamMemberEndDate: '',
      teamMemberStartDate: '',
      yetToAddContactId: '',
      yetToAddDisciplineId: '',
      yetToAddOtherContactId: '',
      yetToAddRoleId: '',
    });
    formikRef.current?.setTouched({});
    setEditOtherRecordIndex(null);
  };
  const handleDeleteOtherContact = (index: number) => {
    setDeleteIndex(index);
    setDeleteWarning(true);
  };
  useEffect(() => {
    if (editRecordIndex !== null) {
      handleEditTeamMemberContact(editRecordIndex);
    }
  }, [editRecordIndex]);
  const handleEditTeamMemberContact = (editIndex: number) => {
    setEditRecordIndex(editIndex);
    setContactType('teamMember');

    const data = ifspTeamTable[editIndex];
    const recordToEdit = employeeListForTeam.filter(
      (employee) => employee.id === data?.id,
    );
    const roleAvailable = recordToEdit[0].roles?.includes(data?.role);
    if (data) {
      setYetToAddOtherContactId(data.id);
      setTeamMembersRole(() => {
        const filteredContactsRoles: OptionType[] = employeeListForTeam
          .filter((contact) => contact.id === data?.id)
          .flatMap((contact) =>
            contact.roles
              ? contact.roles.flatMap((roleString) =>
                  roleString.split(',').map((roleId) => ({
                    id: roleId.trim(),
                    label:
                      employeeRoles.find(
                        (empRole) => empRole.id === roleId.trim(),
                      )?.label ?? '',
                  })),
                )
              : [],
          );
        return filteredContactsRoles;
      });
      if (lookups === null) {
        return;
      }

      if (roleAvailable) {
        setRole(data.role);
        formikRef.current?.setFieldValue('yetToAddRoleId', data.role);
      } else {
        setRole('');
        formikRef.current?.setFieldValue('yetToAddRoleId', '');
      }

      formikRef.current?.setFieldValue('contactType', 'teamMember');
      formikRef.current?.setFieldValue('yetToAddOtherContactId', data?.id);
      formikRef.current?.setFieldValue('teamMemberStartDate', data?.startDate);
      formikRef.current?.setFieldValue('teamMemberEndDate', data?.endDate);

      formikRef.current?.setFieldValue(
        'yetToAddDisciplineId',
        data?.discipline,
      );
      formikRef.current?.setFieldValue('yetToAddContactId', '');
      setTeamMembersStartDate(data?.startDate || '');
      setTeamMembersEndDate(data?.endDate || '');
    }
  };

  const handleEditOtherContact = (editIndex: number) => {
    setEditOtherRecordIndex(editIndex);
    setContactType('other');
    const data = ifspTeamTable[editIndex];
    formikRef.current?.setFieldValue('contactType', 'other');
    formikRef.current?.setFieldValue('othersName', data?.name);
    formikRef.current?.setFieldValue(
      'othersJobDescription',
      data?.jobDescription,
    );
    formikRef.current?.setFieldValue('othersStartDate', data?.startDate);
    formikRef.current?.setFieldValue('othersEndDate', data?.endDate);

    setOthersName(data?.name || '');
    setOthersJobDescription(data?.jobDescription || '');
    setOthersStartDate(data?.startDate || '');
    setOthersEndDate(data?.endDate || '');
  };
  const handleCreateIFSP = () => {
    let updatedIfsp: IfspContextDataType = ifsp;
    if (isDailyRoutineUpdated) {
      const dailyRoutines = updatedIfsp.ifspTeam?.forms?.dailyRoutines || [];
      const dailyRoutineLastIndex = dailyRoutines.length - 1;

      const updatedDailyRoutines = dailyRoutines.map((routine, index) =>
        index === dailyRoutineLastIndex && !routine.sessionReviewedDate
          ? {
              ...routine,
              sessionReviewedDate: `${dayjs().format(
                'MM/DD/YYYY',
              )} ${dayjs().format('hh:mm A')}`,
            }
          : routine,
      );

      updatedIfsp.ifspTeam = {
        ...updatedIfsp.ifspTeam,
        forms: {
          ...updatedIfsp.ifspTeam?.forms,
          dailyRoutines: updatedDailyRoutines,
        },
      };
    }

    if (isHealthAndEligibilityUpdated) {
      const healthSummary = updatedIfsp.ifspTeam?.forms?.healthSummary || [];
      const healthSummaryLastIndex = healthSummary.length - 1;

      const updatedHealthSummary = healthSummary.map((summary, index) =>
        index === healthSummaryLastIndex && !summary.sessionReviewedDate
          ? {
              ...summary,
              sessionReviewedDate: `${dayjs().format(
                'MM/DD/YYYY',
              )} ${dayjs().format('hh:mm A')}`,
            }
          : summary,
      );

      updatedIfsp.ifspTeam = {
        ...updatedIfsp.ifspTeam,
        forms: {
          ...updatedIfsp.ifspTeam?.forms,
          healthSummary: updatedHealthSummary,
        },
      };
    }

    if (isDevelopmentSummaryUpdated) {
      const developmentalSummary =
        updatedIfsp.ifspTeam?.forms?.developmentalSummary || [];
      const developmentalSummaryLastIndex = developmentalSummary.length - 1;

      const updatedDevelopmentalSummary = developmentalSummary.map(
        (summary, index) =>
          index === developmentalSummaryLastIndex &&
          !summary.sessionReviewedDate
            ? {
                ...summary,
                sessionReviewedDate: `${dayjs().format(
                  'MM/DD/YYYY',
                )} ${dayjs().format('hh:mm A')}`,
              }
            : summary,
      );

      updatedIfsp.ifspTeam = {
        ...updatedIfsp.ifspTeam,
        forms: {
          ...updatedIfsp.ifspTeam?.forms,
          developmentalSummary: updatedDevelopmentalSummary,
        },
      };
    }

    if (isFamilyConcernsUpdated) {
      const familyConcerns = updatedIfsp.ifspTeam?.forms?.familyConcerns || [];
      const familyConcernsLastIndex = familyConcerns.length - 1;

      const updatedFamilyConcerns = familyConcerns.map((concern, index) =>
        index === familyConcernsLastIndex && !concern.sessionReviewedDate
          ? {
              ...concern,
              sessionReviewedDate: `${dayjs().format(
                'MM/DD/YYYY',
              )} ${dayjs().format('hh:mm A')}`,
            }
          : concern,
      );

      updatedIfsp.ifspTeam = {
        ...updatedIfsp.ifspTeam,
        forms: {
          ...updatedIfsp.ifspTeam?.forms,
          familyConcerns: updatedFamilyConcerns,
        },
      };
    }
    if (isLatestConsentSigned && !ifsp.isLocked) {
      const consentForChangeForSDP =
        ifsp.ifspTeam?.forms?.consentForChangeForSDP || [];

      const updatedConsentForChangeForSDP = consentForChangeForSDP.map(
        (consent) => {
          const allDates = [
            ...(consent.consentSignature?.map((sig) => sig.date) || []),
            ...(consent.parentSignature?.map((sig) => sig.date) || []),
          ];

          const validDates = allDates.filter((date) => !!date);

          const parsedDates = validDates
            .map((date) => dayjs(date, 'MM/DD/YYYY'))
            .filter((dateObj) => dateObj.isValid())
            .sort((a, b) => a.valueOf() - b.valueOf());

          const highestDate =
            parsedDates.length > 0
              ? parsedDates[parsedDates.length - 1].format('MM/DD/YYYY')
              : null;

          const newDatePart =
            consent.isConsentSkipped && consent.isAdminAmendment
              ? consent.employeeSignature?.date
              : highestDate;

          let timePart = '';

          if (typeof consent.sessionReviewedDate === 'string') {
            // Extract and reuse existing time if present
            const parts = consent.sessionReviewedDate.split(' ');
            timePart = parts.length > 1 ? parts.slice(1).join(' ') : '';
          }

          if (!timePart) {
            // No previous time exists, use current time
            timePart = dayjs().format('hh:mm A');
          }

          return {
            ...consent,
            sessionReviewedDate: `${newDatePart} ${timePart}`,
          };
        },
      );

      const updatedServiceDeliveryPlay =
        ifsp.ifspTeam.forms.serviceDeliveryPlan.map((sdp) => ({
          ...sdp,
          earlyInterventionService: sdp.earlyInterventionService.map((eis) => ({
            ...eis,
            servicePlanId: eis.servicePlanId || uuidv4(),
            isRecordSubmitted: true,
          })),
        }));
      const updatedChildAndFamilyOutcomes =
        ifsp.ifspTeam.forms.childIfspOutcomes.map((outcome) => {
          const outcomeReview = outcome.outcomesProgressReview;

          let outcomeId = '';

          if (outcomeReview.outcomeId && outcomeReview.startDate) {
            outcomeId = outcomeReview.outcomeId;
          } else if (
            outcomeReview.outcomeId === '' &&
            outcomeReview.startDate
          ) {
            outcomeId = uuidv4();
          }

          const updatedOutcomeReview = {
            ...outcomeReview,
            outcomeId,
            isRecordSubmitted: true,
          };

          return {
            ...outcome,
            outcomesProgressReview: updatedOutcomeReview,
          };
        });
      const lockingUserId = localStorage.getItem('userId');
      const formatedUserName = formatName(
        localStorage.getItem('userFirstName'),
        null,
        localStorage.getItem('userLastName'),
        null,
      );
      const updatedReviewedList = [
        ...ifsp.reviewedList,
        {
          reviewedByDiscipline: '',
          reviewedById: lockingUserId,
          reviewedByName: formatedUserName,
          reviewedDate: `${dayjs().format('MM/DD/YYYY')} ${dayjs().format(
            'hh:mm A',
          )}`,
        },
      ];

      updatedIfsp = {
        ...ifsp,
        ifspTeam: {
          ...ifsp.ifspTeam,
          forms: {
            ...ifsp.ifspTeam.forms,
            serviceDeliveryPlan: updatedServiceDeliveryPlay,
            childIfspOutcomes: updatedChildAndFamilyOutcomes,
            consentForChangeForSDP: updatedConsentForChangeForSDP,
          },
        },
        reviewedList: updatedReviewedList,
        isLocked: true,
      };
      updateIfspContext({
        ...ifsp,
        selectedLanguage: selectedLang,
        ifspTeam: {
          ...ifsp.ifspTeam,
          forms: {
            ...ifsp.ifspTeam.forms,
            consentForChangeForSDP: updatedConsentForChangeForSDP,
            serviceDeliveryPlan: updatedServiceDeliveryPlay,
            childIfspOutcomes: updatedChildAndFamilyOutcomes,
          },
        },
        reviewedList: updatedReviewedList,
      });
    } else {
      const consentForChangeForSDP =
        ifsp.ifspTeam?.forms?.consentForChangeForSDP || [];

      const updatedConsentForChangeForSDP = consentForChangeForSDP.map(
        (consent) => {
          const hasSignatures =
            (consent.isConsentSkipped &&
              consent.employeeSignature?.signature) ||
            (consent.consentSignature?.some(
              (x) => x.signature || x.rowKey || x.verbalConsent,
            ) &&
              consent.parentSignature?.some(
                (x) => x.signature || x.rowKey || x.verbalConsent,
              ));

          if (!hasSignatures) return consent;

          const allDates = [
            ...(consent.consentSignature?.map((sig) => sig.date) || []),
            ...(consent.parentSignature?.map((sig) => sig.date) || []),
          ];

          const validDates = allDates.filter((date) => !!date);

          const parsedDates = validDates
            .map((date) => dayjs(date, 'MM/DD/YYYY'))
            .filter((dateObj) => dateObj.isValid())
            .sort((a, b) => a.valueOf() - b.valueOf());

          const highestDate =
            parsedDates.length > 0
              ? parsedDates[parsedDates.length - 1].format('MM/DD/YYYY')
              : null;

          const newDatePart =
            consent.isConsentSkipped && consent.isAdminAmendment
              ? consent.employeeSignature?.date
              : highestDate;

          let timePart = '';

          if (consent.sessionReviewedDate) {
            const reviewedDateStr = String(consent.sessionReviewedDate); // Ensure it's a string
            const parts = reviewedDateStr.split(' ');
            timePart = parts.length > 1 ? parts.slice(1).join(' ') : '';
          } else {
            timePart = dayjs().format('hh:mm A');
          }

          return {
            ...consent,
            sessionReviewedDate: updatedIfsp.lockWithoutConsentSignature
              ? consent.sessionReviewedDate
              : `${newDatePart} ${timePart}`,
          };
        },
      );
      updatedIfsp = {
        ...ifsp,
        selectedLanguage: selectedLang,
        isReportUpdated:
          isDailyRoutineUpdated ||
          isDevelopmentSummaryUpdated ||
          isFamilyConcernsUpdated ||
          isHealthAndEligibilityUpdated ||
          (updatedIfsp.completedBy.id
            ? updatedIfsp.completedBy.id !== completedByBackup.id
            : false),
        ifspTeam: {
          ...ifsp.ifspTeam,
          forms: {
            ...ifsp.ifspTeam.forms,
            dailyRoutines: updatedIfsp.ifspTeam.forms.dailyRoutines,
            healthSummary: updatedIfsp.ifspTeam.forms.healthSummary,
            familyConcerns: updatedIfsp.ifspTeam.forms.familyConcerns,
            developmentalSummary:
              updatedIfsp.ifspTeam.forms.developmentalSummary,
            childIfspOutcomes: ifsp.ifspTeam.forms.childIfspOutcomes.map(
              (outcome) => {
                const outcomeReview = outcome.outcomesProgressReview;

                let outcomeId = '';

                if (outcomeReview.outcomeId && outcomeReview.startDate) {
                  outcomeId = outcomeReview.outcomeId;
                } else if (
                  outcomeReview.outcomeId === '' &&
                  outcomeReview.startDate
                ) {
                  outcomeId = uuidv4();
                }

                return {
                  ...outcome,
                  outcomesProgressReview: {
                    ...outcomeReview,
                    outcomeId,
                  },
                };
              },
            ),
            serviceDeliveryPlan: ifsp.ifspTeam.forms.serviceDeliveryPlan.map(
              (sdp) => ({
                ...sdp,
                earlyInterventionService: sdp.earlyInterventionService.map(
                  (eis) => ({
                    ...eis,
                    servicePlanId: eis.servicePlanId || uuidv4(),
                  }),
                ),
              }),
            ),
            consentForChangeForSDP: updatedConsentForChangeForSDP,
          },
        },
      };
      updateIfspContext({ ...updatedIfsp });
    }
    updateIfspTouched(false);
    checkAndSetIfspScreenFieldLock(ifsp);
    const clientId = localStorage.getItem('ClientId');
    if (clientId) {
      toggleLoader(true);
      createIfsp(
        {
          ...updatedIfsp,
          completedBy: {
            ...updatedIfsp.completedBy,
            label:
              employeeList.find((emp) => emp.id === ifsp.completedBy.id)
                ?.name ?? '',
          },
        },
        clientId,
      )
        .then(() => {
          setSaveClicked((prev) => !prev);
          updateIfspTouched(false);
          setOpen(true);
          formikRef.current?.setTouched({}, false);
          setToastrVariable('success');
          setToastrId('IFSP.savedSuccessfully');
          setToastrDefaultMessage('Data Saved Successfully');
          if (getIfsp) getIfsp(clientId);
          setOpenTransitionModal(false);
          setCloseDailyRoutine(true);
          setCloseHealthEligibility(true);
          setCloseFamilyConcern(true);
          setCloseDevelopmentSummary(true);
          setCloseConsentChangeSDP(true);
          setOpenSDP(false);
          setOpenOutCome(false);
          setOpenTransistionPrep(false);
          toggleLoader(false);
        })
        .catch((error) => {
          setToastrVariable('error');
          toggleLoader(false);
          setOpen(true);
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('IFSP.failedToSave');
            setToastrDefaultMessage('Failed to Save IFSP');
          }
        });
    }
  };

  useEffect(() => {
    setReportCompletedByRole(() => {
      const filteredContactsRoles: OptionType[] = employeeList
        .filter((contact) => contact.id === ifsp.completedBy.id)
        .flatMap((contact) =>
          contact.roles
            ? contact.roles.flatMap((roleString) =>
                roleString.split(',').map((roleId) => ({
                  id: roleId.trim(),
                  label:
                    employeeRoles.find(
                      (empRole) => empRole.id === roleId.trim(),
                    )?.label ?? '',
                })),
              )
            : [],
        );

      return filteredContactsRoles;
    });
    setReportedCompletedBy({
      id: ifsp.completedBy.id,
      label: ifsp.completedBy.label,
    });
    setReportCompletedByRoleId(ifsp.completedByRole);
    setCompletedByBackup({
      id: ifsp.completedBy.id,
      label: ifsp.completedBy.label,
    });
    setParentContactes(() => {
      const formattedContacts: OptionType[] = ifsp.ifspTeam.contact
        .filter(
          (contact) =>
            !ifsp.ifspTeam.parents.some(
              (parent) => parent.id.toString() === contact.id.toString(),
            ),
        )
        .map((filteredContact) => ({
          id: filteredContact.id,
          label: filteredContact.name,
        }));
      return formattedContacts;
    });
  }, [
    ifsp.ifspTeam.contact,
    ifsp.ifspTeam.parents,
    ifsp.ifspTeam.teamMember,
    employeeList,
  ]);
  useEffect(() => {
    const ifspTeamMembers: IfspTeamMembersType[] = [];
    const uniqueMembersSet = new Set<string>();
    const isMemberUnique = (member: IfspTeamMembersType) => {
      return !Array.from(uniqueMembersSet).some((existingMember) => {
        const parsedMember = JSON.parse(existingMember);
        if (member.startDate || member.endDate) {
          return false;
        } else {
          return (
            parsedMember.name === member.name &&
            parsedMember.role === member.role
          );
        }
      });
    };

    const filteredSelectContacts = ifsp.ifspTeam.parents.map((contactId) => {
      return ifsp.ifspTeam.contact.find(
        (contact) => contact.id.toString() === contactId.id.toString(),
      );
    });

    filteredSelectContacts.forEach((contact) => {
      if (contact) {
        const member = {
          id: contact.id,
          name: contact.name,
          email: contact.email,
          phone: contact.phone,
          role: contact.relationshipToClient,
          discipline: contact.type,
          startDate: contact.startDate,
          endDate: contact.endDate,
        };

        if (isMemberUnique(member)) {
          uniqueMembersSet.add(JSON.stringify(member));
          ifspTeamMembers.push(member);
        }
      }
    });

    ifsp.ifspTeam.teamMember.forEach((other) => {
      if (other.discipline) {
        const member = {
          id: other.id,
          name: formatName(
            employeeListForTeam.find((emp) => emp.id === other.id)?.firstName ??
              '',
            null,
            employeeListForTeam.find((emp) => emp.id === other.id)?.lastName ??
              '',
            null,
          ),
          email: other.email,
          phone: other.phone,
          role: other.role,
          discipline: other.discipline,
          startDate: other.startDate,
          endDate: other.endDate,
        };
        if (isMemberUnique(member)) {
          uniqueMembersSet.add(JSON.stringify(member));
          ifspTeamMembers.push(member);
        }
      }
    });

    ifsp.ifspTeam.others.forEach((other, index) => {
      ifspTeamMembers.push({
        id: `${index}`, // or some other identifier
        name: other.name,
        email: '',
        phone: '',
        role: '', // Adjust as needed
        discipline: '',
        jobDescription: other.jobDescription,
        startDate: other.startDate,
        endDate: other.endDate,
      });
    });

    setIfspTeamTable(ifspTeamMembers);
  }, [
    ifsp.ifspTeam.contact,
    ifsp.ifspTeam.parents,
    ifsp.ifspTeam.teamMember,
    employeeListForTeam,
  ]);

  useEffect(() => {
    let recordToEdit: EmployeeListType | null = null;

    if (editRecordIndex !== null) {
      const data = ifspTeamTable[editRecordIndex];
      recordToEdit =
        employeeListForTeam.find((employee) => employee.id === data?.id) ||
        null;
      setTeamMembersDiscipline(() => {
        const disciplines = employeeListForTeam
          .filter((contact) => contact.id === data?.id)
          .flatMap(
            (contact) =>
              contact.disciplineHistoryList?.map((history) => ({
                id: history.disciplineType,
                label:
                  lookups?.employeeDiscipline?.find(
                    (discipline) => discipline.code === history.disciplineType,
                  )?.description ?? '',
              })) || [],
          );

        return Array.from(new Map(disciplines.map((d) => [d.id, d])).values());
      });
    }

    setTeamMembers(() => {
      return employeeListForTeam
        .filter((emp) => {
          // Always include active employees
          if (!emp.isInActive) return true;

          // If editing, also include the inactive one being edited
          if (recordToEdit?.id === emp.id) return true;

          return false;
        })
        .map((emp) => {
          return {
            label: formatName(emp.firstName, null, emp.lastName, null),
            id: emp.id,
          };
        });
    });
  }, [employeeListForTeam, editRecordIndex, ifspTeamTable]);

  useEffect(() => {
    updateIfspContext({
      ...ifsp,
      ifspMeetingDate:
        ifsp.ifspMeetingDate !== '' || null
          ? ifsp.ifspMeetingDate
          : dayjs().format('MM/DD/YYYY'),
    });
    if (ifsp.ifspDueDate !== '') {
      if (dayjs(ifsp.ifspMeetingDate).isAfter(dayjs(ifsp.ifspDueDate))) {
        setIsIfspDueDatePassed(true);
      } else {
        updateIfspContext({
          ...ifsp,
          reasonForDelay: { id: '', label: '' },
        });
        setIsIfspDueDatePassed(false);
      }
    }
  }, [ifsp.ifspMeetingDate]);
  useEffect(() => {
    if (ifsp.ifspDueDate !== '') {
      if (dayjs(ifsp.ifspMeetingDate).isAfter(dayjs(ifsp.ifspDueDate))) {
        setIsIfspDueDatePassed(true);
      }
    }
  }, [ifsp.ifspDueDate]);
  useEffect(() => {
    setReportCompletedByRoleId(ifsp.completedByRole);
    setEmployeeList(employeeData);
  }, [employeeData]);

  useEffect(() => {
    const shouldReset =
      (isDailyRoutineUpdated ||
        isDevelopmentSummaryUpdated ||
        isFamilyConcernsUpdated ||
        isHealthAndEligibilityUpdated) &&
      !isUserManuallyAddedReportedBy;

    const alreadyCompletedByReset =
      ifsp?.completedBy?.id === '' && ifsp?.completedBy?.label === '';

    const alreadyCompletedByRoleReset = ifsp?.completedByRole === '';

    const setMandatoryFields = () => {
      setIsreportCompletedByMandatory(true);
      setIsreportCompletedRoleMandatory(true);
    };

    if (
      shouldReset &&
      !alreadyCompletedByReset &&
      !alreadyCompletedByRoleReset
    ) {
      updateIfspContext({
        ...ifsp,
        completedBy: { id: '', label: '' },
        completedByRole: '',
      });

      setReportedCompletedBy({ id: '', label: '' });
      setReportCompletedByRoleId('');
      setMandatoryFields();
    } else if (
      alreadyCompletedByReset &&
      (isDailyRoutineUpdated ||
        isDevelopmentSummaryUpdated ||
        isFamilyConcernsUpdated ||
        isHealthAndEligibilityUpdated)
    ) {
      setMandatoryFields();
    } else if (
      alreadyCompletedByRoleReset &&
      (isDailyRoutineUpdated ||
        isDevelopmentSummaryUpdated ||
        isFamilyConcernsUpdated ||
        isHealthAndEligibilityUpdated)
    ) {
      setIsreportCompletedRoleMandatory(true);
    } else if (alreadyCompletedByRoleReset && !alreadyCompletedByReset) {
      setIsreportCompletedRoleMandatory(true);
    } else if (
      isUserManuallyAddedReportedBy ||
      (!isDailyRoutineUpdated &&
        !isDevelopmentSummaryUpdated &&
        !isFamilyConcernsUpdated &&
        !isHealthAndEligibilityUpdated)
    ) {
      setIsreportCompletedByMandatory(false);
      setIsreportCompletedRoleMandatory(false);
    } else {
      setMandatoryFields();
    }
  }, [
    isDailyRoutineUpdated,
    isDevelopmentSummaryUpdated,
    isFamilyConcernsUpdated,
    isHealthAndEligibilityUpdated,
    isUserManuallyAddedReportedBy,
    ifsp,
  ]);

  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleDelaySave = () => {
    updateIfspContext({
      ...ifsp,
      reasonForDelay: reasonForDelay,
    });
    setOpenDelayModal(false);
  };

  const handleDownload = () => {
    toggleLoader(true);
    const clientId = localStorage.getItem('ClientId');
    const localTimezone = dayjs.tz.guess();
    if (clientId) {
      downloadIfsp(clientId, localTimezone)
        .then((response) => {
          const blob = new Blob([response], { type: 'application/pdf' });

          // Create a Blob URL
          const blobUrl = URL.createObjectURL(blob);

          // Open the Blob URL in a new tab
          const anchor = document.createElement('a');
          anchor.href = blobUrl;
          anchor.target = '_blank';
          anchor.download = getClientDocumentFileName(
            `Individualized Family Service Plan (IFSP).pdf`,
          ); // Set desired file name here

          // Append anchor to document body
          document.body.appendChild(anchor);

          // Trigger anchor click
          anchor.click();

          window.open(blobUrl, '_blank');

          // Clean up after use
          URL.revokeObjectURL(blobUrl);
          document.body.removeChild(anchor);

          toggleLoader(false);
        })
        .catch((error) => {
          toggleLoader(false);
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('ConsentForms.downloadErrorMsg');
            setToastrDefaultMessage('Failed to download');
          }
        });
    }
  };

  const ifspScreenLockedToaster = () => {
    return (
      <Box
        className="flex__ align__items__center pt-sm pb-sm pl-md pr-md"
        sx={{ gap: '8px', backgroundColor: '#2A4241', borderRadius: '60px' }}
      >
        <Lock />
        <MediumTypography
          labelid="IFSP.LockedToaster"
          defaultlabel="Locked"
          textColor="#fff"
        />
      </Box>
    );
  };

  const closeIfspReviewedOnListModal = () => {
    setOpenIfspReviewedOnListModal(false);
  };
  const ifspId = useRef('IFSP');

  useEffect(() => {
    if (isCancelClicked) {
      setTimeout(() => {
        setRefresh((prev) => prev + 1);
      }, 2000);
      setIsCancelClicked(false);
    }
  }, [isCancelClicked, ifspTouched]);
  return (
    <Box component="main">
      {selectedLang !== '' && (
        <TranslateWidget
          initialLanguage={'en'}
          screenId={ifspId.current}
          languageChangeCallback={(language) => {
            setSelectedLang(language);
          }}
        >
          {/* <Box component="section" key={refresh}> */}
          <Box component="section">
            <ModalPopup
              open={openModal}
              description="formUnsavedChangesMessage"
              onCancel={() => setOpenModal(false)}
              onOk={() => {
                setIsUserManuallyAddedReportedBy(true);
                setIsCancelClicked(true);
                const clientId = localStorage.getItem('ClientId');
                if (clientId && getIfsp) {
                  setIsreportCompletedByMandatory(false);
                  setIsreportCompletedRoleMandatory(false);
                  setOpenTransitionModal(false);
                  setCloseDailyRoutine(true);
                  setCloseHealthEligibility(true);
                  setCloseFamilyConcern(true);
                  setCloseDevelopmentSummary(true);
                  setCloseConsentChangeSDP(true);
                  setOpenSDP(false);
                  setOpenOutCome(false);
                  setOpenTransistionPrep(false);
                  getIfsp(clientId);
                }
                setOpenModal(false);
              }}
              labelId1="Clientpage.cancelbtn"
              negativeActionLabel="Cancel"
              labelId2="Clientpage.Okbtn"
              positiveActionLabel="Ok"
            />
            <IfspUpdateModalPopup
              openIfspUpdateModal={openUpdateModal}
              onCancel={() => setOpenUpdateModal(false)}
              onOk={() => {
                updateIfspContext({
                  ...ifsp,
                  isLocked: false,
                  ifspTeam: {
                    ...ifsp.ifspTeam,
                    forms: {
                      ...ifsp.ifspTeam.forms,
                      consentForChangeForSDP: [
                        ...ifsp.ifspTeam.forms.consentForChangeForSDP,
                        {
                          changes: '',
                          isAdminAmendment: false,
                          isConsentSkipped: false,
                          isEligibleUntilThirdBirthday: '',
                          eligibleTool: { id: '', label: '' },
                          isEligibleFor: '',
                          assessmentCompletionDate: null,
                          reEstablishedDate: null,
                          parentInitial: { id: '', label: '' },
                          consentType: 'consentForChange',
                          sessionReviewedDate: null,
                          participated: false,
                          agreeDisagree: '',
                          exceptionList: '',
                          employeeSignature: {
                            id: '',
                            isUpdated: false,
                            signature: '',
                            verbalConsent: false,
                            date: null,
                            partitionKey: '',
                            rowKey: '',
                          },
                          parentSignature: [
                            {
                              id: 'p.0',
                              isUpdated: false,
                              signature: '',
                              verbalConsent: false,
                              date: null,
                              partitionKey: '',
                              rowKey: '',
                            },
                            {
                              id: 'p.1',
                              isUpdated: false,
                              signature: '',
                              verbalConsent: false,
                              date: null,
                              partitionKey: '',
                              rowKey: '',
                            },
                          ],
                          consent: false,
                          consentSignature: [
                            {
                              id: 'c.0',
                              isUpdated: false,
                              signature: '',
                              verbalConsent: false,
                              date: null,
                              partitionKey: '',
                              rowKey: '',
                            },
                            {
                              id: 'c.1',
                              isUpdated: false,
                              signature: '',
                              verbalConsent: false,
                              date: null,
                              partitionKey: '',
                              rowKey: '',
                            },
                          ],
                        },
                      ],
                    },
                  },
                });
                setOpenUpdateModal(false);
                setIfspScreenFieldLock(false);
              }}
              labelId1="Clientpage.cancelbtn"
              negativeActionLabel="Cancel"
              labelId2="Clientpage.Okbtn"
              positiveActionLabel="Ok"
            />
            {ifsp.reviewedList.length > 0 && (
              <IfspReviewedOnListModal
                data={ifsp.reviewedList[openIfspReviewedOnListClickedIndex]}
                closeIfspReviewedOnListModal={closeIfspReviewedOnListModal}
                openIfspReviewedOnListModal={openIfspReviewedOnListModal}
              />
            )}
            <Dialog open={openDelayModal} fullWidth>
              <DialogTitle>
                <MediumTypography
                  labelid="IFSP.ChooseTheReasonForDelay"
                  defaultlabel="Choose the reason for delay"
                  fontSize="24px"
                  fontweight={700}
                  textColor="#2A4241"
                />
              </DialogTitle>
              <DialogContent>
                <Box className={'flex__ pb-sm'}>
                  <Box component="div" className="flex__ ">
                    <MediumTypography
                      labelid="IFSP.IFSPdueDate"
                      defaultlabel="IFSP due date:"
                      textColor="#97A6A5"
                      fontSize="16px"
                    />
                    <MediumTypography
                      fontSize="16px"
                      textColor="#2A4241"
                      className="pl-xs pr-lg"
                      label={ifsp.ifspDueDate as string}
                    />
                  </Box>
                  <Box component="div" className="flex__ ">
                    <MediumTypography
                      labelid="IFSP.firstmeetingDate"
                      defaultlabel="First Meeting date:"
                      textColor="#97A6A5"
                      fontSize="16px"
                    />
                    <MediumTypography
                      fontSize="16px"
                      textColor="#2A4241"
                      className="pl-xs pr-lg"
                      label={ifsp.ifspMeetingDate?.toString()}
                    />
                  </Box>
                </Box>
                <AutocompleteSearch
                  labelId={'IFSP.SelecttheReasonforDelay'}
                  defaultId={'Select the reason for delay'}
                  selectedValue={{
                    id:
                      reasonData.find((x) => x.id === reasonForDelay.id)?.id ??
                      '',
                    label:
                      reasonData.find((x) => x.id === reasonForDelay.id)
                        ?.label ?? '',
                  }}
                  data={activeReasonData}
                  onSelectItem={(value: OptionType | null) => {
                    if (value) {
                      setReasonForDelay({
                        id: value.id,
                        label: value.label,
                      });
                    } else {
                      setReasonForDelay({ id: '', label: '' });
                    }
                  }}
                  disableLabel={false}
                  disabled={isOtherLanguage()}
                />
                <Box className="pt-lg flex__ justifyContent-FlexEnd">
                  <ButtonComponent
                    className="btn-primary btn-cancel"
                    labelId={'cancelText'}
                    defaultLabelId="Cancel"
                    variantType="outlined"
                    onClick={() => setOpenDelayModal(false)}
                  />
                  <ButtonComponent
                    variantType="contained"
                    type="submit"
                    className="btn-primary btn-submit alrt_Width_btn ml-md"
                    labelId="saveText"
                    defaultLabelId="Save"
                    onClick={handleDelaySave}
                    disabled={reasonForDelay?.id === ''}
                  />
                </Box>
              </DialogContent>
            </Dialog>
            <SnackBarComponent
              open={open}
              handleClose={handleCloseSnakBar}
              successOrError={toastrVariable}
              labelId={toastrId}
              defaultMessageId={toastrDefaultMessage}
            />
            <ModalPopup
              open={openUnSavedModal}
              description="UnsavedAlertDescriptionWhileLeaving"
              labelId1="UnSavedAlertCancel"
              labelId2="UnSavedAlertLeave"
              negativeActionLabel="No"
              positiveActionLabel="Yes"
              onCancel={() => {
                setOpenUnSavedModal(false);
              }}
              onOk={() => {
                if (!isIfspInitiated || ifsp.id) {
                  handleUpdateUnsavedChanges(false);
                  navigation(FACESHEET);
                } else {
                  setIsIfspInitiated(false);
                }
              }}
            />
            <Box component="div" className="rowContainer">
              <Grid container>
                <Grid item xs={6} lg={6}>
                  <Box className="flex__  align__items__center">
                    <CustomBackArrow
                      onClick={() => {
                        if (hasUnsavedChanges) {
                          setOpenUnSavedModal(true);
                          return;
                        }

                        if (!isIfspInitiated || ifsp.id) {
                          handleUpdateUnsavedChanges(false);
                          navigation(FACESHEET);
                        } else {
                          setIsIfspInitiated(false);
                        }
                      }}
                    />
                    <TitleText
                      labelid="ifsp.Title"
                      defaultlabel="Individualized Family Service Plan (IFSP)"
                      className="ml-md"
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={6}
                  lg={6}
                  className="flex__ align__items__center justifyContent-FlexEnd"
                >
                  <Box component="div" className="flex__">
                    {!offlineMode && isIfspInitiated && (
                      <ButtonComponent
                        className="btn-primary btn-submit"
                        labelId="ConsentForms.listActionDownloadToolTipLabel"
                        defaultLabelId="Download"
                        variantType="contained"
                        onClick={handleDownload}
                      />
                    )}
                    {!offlineMode && isIfspInitiated && (
                      <ButtonComponent
                        className="btn-primary btn-submit ml-xs"
                        labelId="ConsentForms.listActionShareToolTipLabel"
                        defaultLabelId="Share"
                        variantType="contained"
                        onClick={() => setShareModal(true)}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
            {!isIfspInitiated ? (
              <EmptyScreen
                titleLabelId={'IFSP.NotInitiatedTitle'}
                defaultTitleText="IFSP not initiated yet"
                showButton={hasEditPermission}
                buttonLabelId="IFSP.InitiateButtonTitle"
                defaultButtonTitleText="Initiate IFSP"
                onButtonClick={() => setIsIfspInitiated(true)}
                className="mb-md"
              />
            ) : (
              <Box component="div">
                <Formik
                  initialValues={{
                    contactType: contactType,
                    yetToAddContactId: yetToAddContactId,
                    yetToAddOtherContactId: yetToAddOtherContactId,
                    yetToAddRoleId: yetToAddRoleId,
                    yetToAddDisciplineId: yetToAddDisciplineId,
                    teamMemberStartDate: teamMembersStartDate,
                    teamMemberEndDate: teamMembersEndDate,
                    othersName: othersName,
                    othersJobDescription: othersJobDescription,
                    othersEndDate: othersEndDate,
                    othersStartDate: othersStartDate,
                  }}
                  validate={validateDate}
                  innerRef={formikRef}
                  validationSchema={validationSchema}
                  onSubmit={(values) => {
                    if (values.contactType === 'parent') {
                      handleAddNewContact();
                    } else if (values.contactType === 'teamMember') {
                      handleAddNewTeamMemberContact();
                    } else {
                      handelAddOtherContact();
                    }
                  }}
                >
                  {({
                    values,
                    setFieldValue,
                    errors,
                    touched,
                    handleSubmit,
                  }) => (
                    <Box component="div">
                      <ModalPopup
                        open={showAlertDialog}
                        description="duplicateWarningMessage"
                        onOk={() => {
                          setShowAlertDialog(false);
                        }}
                        labelId2="Clientpage.Okbtn"
                        positiveActionLabel="Ok"
                      />
                      <ModalPopup
                        open={deleteWarning}
                        description="deleteConfirmationMessage"
                        onOk={() => {
                          const updatedTeamMembers = ifspTeamTable.filter(
                            (__, i) => i !== deleteIndex,
                          );
                          updateIfspContext({
                            ...ifsp,
                            ifspTeam: {
                              ...ifsp.ifspTeam,
                              parents: updatedTeamMembers.filter(
                                (item) =>
                                  typeof item.id !== 'number' &&
                                  item.startDate === undefined &&
                                  !item.jobDescription,
                              ),
                              teamMember: updatedTeamMembers.filter(
                                (item) =>
                                  typeof item.id !== 'number' &&
                                  item.startDate &&
                                  !item.jobDescription,
                              ),
                              others: updatedTeamMembers
                                .filter(
                                  (item) => !item.discipline && item.startDate,
                                )
                                .map((item) => ({
                                  name: item.name,
                                  jobDescription: item.jobDescription ?? '',
                                  startDate: item.startDate,
                                  endDate: item.endDate,
                                })),
                            },
                          });
                          setEditRecordIndex(null);
                          setTeamMembersRole([]);
                          setTeamMembersDiscipline([]);
                          setTeamMembersStartDate('');
                          setTeamMembersEndDate('');
                          setYetToAddContactId('');
                          setYetToAddOtherContactId('');
                          setYetToAddRoleId('');
                          setYetToAddDisciplineId('');

                          formikRef.current?.resetForm(); // Automatically resets all fields to their initial values
                          formikRef.current?.setValues({
                            contactType: formikRef.current?.values.contactType, // Preserving specific values if needed
                            teamMemberEndDate: '',
                            teamMemberStartDate: '',
                            yetToAddContactId: '',
                            yetToAddDisciplineId: '',
                            yetToAddOtherContactId: '',
                            yetToAddRoleId: '',
                            othersEndDate: '',
                            othersStartDate: '',
                            othersJobDescription: '',
                            othersName: '',
                          });

                          formikRef.current?.setTouched({});
                          setEditOtherRecordIndex(null);
                          setDeleteWarning(false);
                        }}
                        onCancel={() => {
                          setDeleteWarning(false);
                        }}
                        labelId1="Clientpage.cancelbtn"
                        negativeActionLabel="cancelText"
                        labelId2="Clientpage.Okbtn"
                        positiveActionLabel="Ok"
                      />

                      {infoModal && (
                        <InformationModal
                          open={infoModal}
                          titleLabel="Information"
                          titleDefaultLabel="Information"
                          buttonLabel="Ok"
                          buttonDefaultLabel="Ok"
                          onClose={handleCloseInfoModal}
                          info={info}
                        />
                      )}
                      <Card className="formCardview">
                        <Box
                          component="div"
                          className="flex__  mb-md alignItemCenter justifyContentSpaceBtw"
                        >
                          <Box className="flex__ alignItemCenter">
                            <DPHLogo
                              style={{ width: '110px', height: '110px' }}
                            />
                            <MediumTypography
                              labelid="ifsp.FormTitle"
                              defaultlabel="Massachusetts Department of Public Health Individualized Family Service Plan"
                              className="ml-md"
                              fontweight={600}
                              fontSize="20px"
                            />
                          </Box>
                          <InfoIcon
                            onClick={handleOpenInfoModal}
                            className="cursorPointer"
                          />
                        </Box>

                        <SingleLine className="width__100" />
                        <Box component="div" className="mt-sm mb-sm ">
                          <ProgramPanel program={ifsp.programDetails} />
                        </Box>
                        <SingleLine className="width__100" />
                        <Box component="div" className="flex__ mt-sm mb-sm ">
                          <Box component="div" className="flex__ ">
                            <MediumTypography
                              labelid="ifsp.ClientName"
                              defaultlabel="Client Name:"
                              textColor="#97A6A5"
                              fontSize="16px"
                            />

                            <Tooltip
                              className="minWidthContent"
                              title={
                                ifsp.client.name && ifsp.client.name.length > 10
                                  ? `${ifsp.client.name}`
                                  : ''
                              }
                              followCursor
                              arrow={true}
                            >
                              <Box component="div" className="width150px ">
                                <MediumTypography
                                  fontSize="16px"
                                  sxProps={{
                                    paddingLeft:
                                      ifsp.client.name === '' ||
                                      ifsp.client.name === null
                                        ? '24px'
                                        : '6px',
                                    color: '#2A4241',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                  }}
                                  label={
                                    ifsp.client.name === '' ||
                                    ifsp.client.name === null
                                      ? '-'
                                      : `${ifsp.client.name}`
                                  }
                                />
                              </Box>
                            </Tooltip>
                          </Box>
                          <Box component="div" className="flex__">
                            <MediumTypography
                              className="ml-sm"
                              labelid="ifsp.Sex"
                              defaultlabel="Sex:"
                              textColor="#97A6A5"
                              fontSize="16px"
                            />
                            <MediumTypography
                              fontSize="16px"
                              textColor="#2A4241"
                              className="pr-lg"
                              sxProps={{
                                paddingLeft:
                                  ifsp.client.sex === '' ||
                                  ifsp.client.sex === null
                                    ? '24px'
                                    : '6px',
                              }}
                              label={
                                ifsp.client.sex === '' ||
                                ifsp.client.sex === null
                                  ? '-'
                                  : convertGenderToFrontend(ifsp.client.sex)
                              }
                            />
                          </Box>
                          <Box component="div" className="flex__">
                            <MediumTypography
                              labelid="ifsp.DPHID"
                              defaultlabel="DPH ID:"
                              textColor="#97A6A5"
                              fontSize="16px"
                            />
                            <MediumTypography
                              fontSize="16px"
                              textColor="#2A4241"
                              className="pl-xs pr-lg"
                              label={
                                ifsp.client.dphId === '' ||
                                ifsp.client.dphId === null
                                  ? '-'
                                  : ifsp.client.dphId
                              }
                            />
                          </Box>
                          <Box component="div" className="flex__">
                            <MediumTypography
                              labelid="ifsp.DateofBirth"
                              defaultlabel="Date of Birth:"
                              textColor="#97A6A5"
                              fontSize="16px"
                            />
                            <MediumTypography
                              fontSize="16px"
                              textColor="#2A4241"
                              className="pl-xs pr-lg"
                              label={
                                ifsp.client.dob === '' ||
                                ifsp.client.dob === null
                                  ? '-'
                                  : ifsp.client.dob
                              }
                            />
                          </Box>
                          <Box
                            component="div"
                            className="flex__ visibility__hidden"
                          >
                            <MediumTypography
                              labelid="ifsp.Status"
                              defaultlabel="Status:"
                              textColor="#97A6A5"
                              fontSize="16px"
                            />
                            <MediumTypography
                              fontSize="16px"
                              textColor="#2A4241"
                              className="pl-xs pr-lg"
                              label={'jo'}
                            />
                          </Box>
                        </Box>
                        <SingleLine className="width__100" />
                        <Grid
                          container
                          className="mt-md mb-md"
                          style={{
                            pointerEvents: hasEditPermission ? 'auto' : 'none',
                            opacity: hasEditPermission ? 'unset' : '0.8',
                          }}
                        >
                          <Grid container rowSpacing={'24px'} className="pt-md">
                            <Grid item xs={4}>
                              <DropdownComponent
                                names={primarySettingCode}
                                labelid="IFSP.PrimarySettingCode"
                                defaultlabelid="Primary Settings Code"
                                value={ifsp.primarySettingCode}
                                handleChange={(value) => {
                                  if (
                                    value === 'HOME' ||
                                    value === 'COMMUNITY'
                                  ) {
                                    updateIfspContext({
                                      ...ifsp,
                                      primarySettingCode: value,
                                      primarySettingCodeJustification: '',
                                    });
                                  } else {
                                    updateIfspContext({
                                      ...ifsp,
                                      primarySettingCode: value,
                                    });
                                  }
                                }}
                                disabled={
                                  isOtherLanguage() || !hasEditPermission
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        {ifsp.primarySettingCode != 'HOME' &&
                        ifsp.primarySettingCode != 'COMMUNITY' &&
                        ifsp.primarySettingCode ? (
                          <Box
                            sx={{
                              width: '70%',
                              marginY: '24px',
                              pointerEvents: hasEditPermission
                                ? 'auto'
                                : 'none',
                              opacity: hasEditPermission ? 'unset' : '0.8',
                            }}
                            ref={parentRef}
                            onKeyDownCapture={(e) => {
                              if (isOtherLanguage()) {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            }}
                          >
                            <CommentTextbox
                              Value={ifsp.primarySettingCodeJustification}
                              placeholder="Justification"
                              name="justification"
                              maxLength={500}
                              handlechange={(e) => {
                                updateIfspContext({
                                  ...ifsp,
                                  primarySettingCodeJustification: e,
                                });
                              }}
                              Required
                              rows={2}
                              disabled={isOtherLanguage()}
                            />
                          </Box>
                        ) : null}
                        {ifsp.reasonForDelay.id !== '' &&
                          isIfspDueDatePassed && (
                            <Box
                              component="div"
                              className="flex__ pt-sm pb-sm borderRadius4 justifyContent-Center alignItemCenter"
                              sx={{
                                backgroundColor: '#ECF9F8',
                                pointerEvents: hasEditPermission
                                  ? 'auto'
                                  : 'none',
                                opacity: hasEditPermission ? 'unset' : '0.8',
                              }}
                            >
                              <MediumTypography
                                labelid="IFSP.ReasonForLateIFSPMeeting"
                                defaultlabel="Reason for late IFSP Meeting:"
                                textColor="#97A6A5"
                                fontweight={600}
                                fontSize="14px"
                              />
                              <MediumTypography
                                fontSize="14px"
                                textColor="#2A4241"
                                fontweight={600}
                                className="pl-xs pr-md"
                                label={
                                  reasonData.find(
                                    (x) => x.id === ifsp.reasonForDelay.id,
                                  )?.label
                                }
                              />
                              {((!ifsp.ifspTeam.forms.consentForChangeForSDP[0]
                                .sessionReviewedDate &&
                                !isOtherLanguage()) ||
                                canEditAfterLock) && (
                                <MediumTypography
                                  labelid="IFSP.UpdateComment"
                                  defaultlabel="Update Comment:"
                                  textColor="#00C6B8"
                                  fontSize="14px"
                                  fontweight={600}
                                  className="cursorPointer"
                                  onClick={() => {
                                    setReasonForDelay({
                                      id: ifsp.reasonForDelay.id,
                                      label: ifsp.reasonForDelay.label
                                        ? ifsp.reasonForDelay.label
                                        : reasonData.find(
                                            (x) =>
                                              x.id === ifsp.reasonForDelay.id,
                                          )?.label ?? '',
                                    });
                                    setOpenDelayModal(true);
                                  }}
                                  sxProps={{
                                    textDecorationLine: 'underline',
                                    textUnderlineOffset: '3px',
                                  }}
                                />
                              )}
                            </Box>
                          )}
                        {ifsp.reviewedList.length > 0 && (
                          <Box
                            component="div"
                            className="mt-md"
                            sx={{
                              borderTop: '1px solid #CFDEDE',
                              pointerEvents: hasEditPermission
                                ? 'auto'
                                : 'none',
                              opacity: hasEditPermission ? 'unset' : '0.8',
                            }}
                          >
                            <MediumTypography
                              labelid="ifsp.reviewedon"
                              defaultlabel="Reviewed on"
                              fontSize="18px"
                              fontweight={600}
                              textColor="#2A4241"
                              className="pb-md pt-md"
                            />
                            <Box
                              className="flex__ flex__wrap"
                              sx={{ rowGap: '8px' }}
                            >
                              {ifsp.reviewedList.map((date, index) => (
                                <Box
                                  className="flex__ align__items__center mr-sm"
                                  key={`reviewed-${index + 1}`}
                                  sx={{
                                    backgroundColor: '#E7F0F0',
                                    padding: '5px',
                                    borderRadius: '4px',
                                  }}
                                >
                                  <MediumTypography
                                    label={
                                      date.reviewedDate
                                        ?.toString()
                                        .split(' ')[0] as string
                                    }
                                    fontSize="14px"
                                    fontweight={400}
                                    textColor="#2A4241"
                                    sxProps={{ paddingRight: '5px' }}
                                  />
                                  <File
                                    onClick={() => {
                                      setOpenIfspReviewedOnListClickedIndex(
                                        index,
                                      );
                                      setOpenIfspReviewedOnListModal(false);
                                    }}
                                  />
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                        <Box
                          component="div"
                          className="mt-md"
                          sx={{
                            borderTop: '1px solid #CFDEDE',
                            pointerEvents: hasEditPermission ? 'auto' : 'none',
                            opacity: hasEditPermission ? 'unset' : '0.8',
                          }}
                        >
                          <MediumTypography
                            labelid="ifsp.MembersTitle"
                            defaultlabel="IFSP Team Members: (including family members, caregivers, EI providers, SSP and others)"
                            fontSize="18px"
                            fontweight={600}
                            textColor="#2A4241"
                            className="pb-md pt-md"
                          />
                          <CustomRadioButton
                            data={IfspRadioData}
                            value={values.contactType}
                            onSelectItem={(value) => {
                              formikRef.current?.setTouched({});
                              if (value) {
                                setEditRecordIndex(null);
                                setTeamMembersRole([]);
                                setTeamMembersDiscipline([]);
                                setTeamMembersStartDate('');
                                setTeamMembersEndDate('');
                                setYetToAddContactId('');
                                setYetToAddOtherContactId('');
                                setYetToAddRoleId('');
                                setYetToAddDisciplineId('');

                                formikRef.current?.resetForm(); // Automatically resets all fields to their initial values
                                formikRef.current?.setValues({
                                  contactType: value, // Preserving specific values if needed
                                  teamMemberEndDate: '',
                                  teamMemberStartDate: '',
                                  yetToAddContactId: '',
                                  yetToAddDisciplineId: '',
                                  yetToAddOtherContactId: '',
                                  yetToAddRoleId: '',
                                  othersEndDate: '',
                                  othersStartDate: '',
                                  othersJobDescription: '',
                                  othersName: '',
                                });

                                formikRef.current?.setTouched({});
                                setEditOtherRecordIndex(null);
                                setDeleteWarning(false);

                                setContactType(value);
                                setFieldValue('contactType', value);
                              }
                            }}
                            disable={isOtherLanguage()}
                          />
                        </Box>
                        {contactType === 'parent' && (
                          <Box component="div">
                            <Grid
                              container
                              direction={'row'}
                              className="mt-md mb-lg"
                              sx={{
                                pointerEvents: hasEditPermission
                                  ? 'auto'
                                  : 'none',
                                opacity: hasEditPermission ? 'unset' : '0.8',
                              }}
                            >
                              <Grid item xs={5}>
                                <AutocompleteSearch
                                  data={parentContactes}
                                  labelId="IFSP.selectName"
                                  defaultId="Select Name"
                                  Required={true}
                                  selectedValue={{
                                    id:
                                      parentContactes.find(
                                        (item) => item.id === yetToAddContactId,
                                      )?.id ?? '',
                                    label:
                                      parentContactes.find(
                                        (item) => item.id === yetToAddContactId,
                                      )?.label ?? '',
                                  }}
                                  onSelectItem={(e) => {
                                    if (e) {
                                      setYetToAddContactId(e.id);
                                      setFieldValue('yetToAddContactId', e.id);
                                    } else {
                                      setYetToAddContactId('');
                                      setFieldValue('yetToAddContactId', '');
                                    }
                                  }}
                                  disableLabel={false}
                                  disabled={
                                    isOtherLanguage() || !hasEditPermission
                                  }
                                />
                                {errors.yetToAddContactId &&
                                  touched.yetToAddContactId && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.yetToAddContactId}
                                      defaultlabel="Guardian is required"
                                    />
                                  )}
                              </Grid>
                              {isOtherLanguage() ? null : (
                                <Grid
                                  item
                                  xs={1}
                                  className="cursorPointer align__items__center flex__justify__center"
                                  onClick={() => {
                                    handleSubmit();
                                  }}
                                >
                                  <Box
                                    component="div"
                                    className="flex__ mt-xs "
                                  >
                                    <AddNew className="ml-sm" />
                                    <MediumTypography
                                      labelid="IFSP.Add"
                                      defaultlabel="Add"
                                      className="pl-sm"
                                    />
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        )}
                        {contactType === 'teamMember' && (
                          <Box component="div" className="mt-md mb-md">
                            <Grid
                              container
                              columnSpacing={'32px'}
                              sx={{
                                pointerEvents: hasEditPermission
                                  ? 'auto'
                                  : 'none',
                                opacity: hasEditPermission ? 'unset' : '0.8',
                              }}
                            >
                              <Grid item xs={5}>
                                <LazyAutocompleteSearch
                                  labelId="IFSP.selectName"
                                  defaultId="Select Name"
                                  value={selectedMember}
                                  onChange={(e) => {
                                    if (e) {
                                      setTeamMembers(() => {
                                        return employeeListForTeam.map(
                                          (data) => {
                                            return {
                                              label:
                                                data.lastName +
                                                ', ' +
                                                data.firstName,
                                              id: data.id,
                                            };
                                          },
                                        );
                                      });
                                      setTeamMembersRole(() => {
                                        const filteredContactsRoles: OptionType[] =
                                          employeeListForTeam
                                            .filter(
                                              (contact) => contact.id === e.id,
                                            )
                                            .flatMap((contact) =>
                                              contact.roles
                                                ? contact.roles.flatMap(
                                                    (roleString) =>
                                                      roleString
                                                        .split(',')
                                                        .map((roleId) => ({
                                                          id: roleId.trim(),
                                                          label:
                                                            employeeRoles.find(
                                                              (empRole) =>
                                                                empRole.id ===
                                                                roleId.trim(),
                                                            )?.label ?? '',
                                                        })),
                                                  )
                                                : [],
                                            );
                                        return filteredContactsRoles;
                                      });
                                      setTeamMembersDiscipline(() => {
                                        const disciplines = employeeListForTeam
                                          .filter(
                                            (contact) => contact.id === e.id,
                                          )
                                          .flatMap(
                                            (contact) =>
                                              contact.disciplineHistoryList?.map(
                                                (history) => ({
                                                  id: history.disciplineType,
                                                  label:
                                                    lookups?.employeeDiscipline.find(
                                                      (dis) =>
                                                        dis.code ===
                                                        history.disciplineType,
                                                    )?.description ?? '',
                                                }),
                                              ) || [],
                                          );

                                        return Array.from(
                                          new Map(
                                            disciplines.map((d) => [d.id, d]),
                                          ).values(),
                                        );
                                      });

                                      setFieldValue(
                                        'yetToAddOtherContactId',
                                        e.id,
                                      );
                                      setYetToAddOtherContactId(e.id);
                                    } else {
                                      setYetToAddOtherContactId('');
                                      setTeamMembersRole([]);
                                      setTeamMembersDiscipline([]);
                                      setYetToAddDisciplineId('');
                                      setRole('');
                                      setYetToAddRoleId('');
                                      formikRef.current?.setValues({
                                        ...formikRef.current?.values,
                                        yetToAddOtherContactId: '',
                                        yetToAddDisciplineId: '',
                                        yetToAddRoleId: '',
                                      });
                                    }
                                  }}
                                  fetchOptions={fetchEmployees}
                                  required
                                  disabled={
                                    isOtherLanguage() ||
                                    editRecordIndex !== null
                                  }
                                />
                                {errors.yetToAddOtherContactId &&
                                  touched.yetToAddOtherContactId && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.yetToAddOtherContactId}
                                      defaultlabel="Team Member Name is required"
                                    />
                                  )}
                              </Grid>
                              <Grid item xs={5} display={'none'}>
                                <AutocompleteSearch
                                  data={teamMembers}
                                  labelId="IFSP.selectName"
                                  defaultId="Select Name*"
                                  selectedValue={{
                                    id:
                                      teamMembers.find(
                                        (item) =>
                                          item.id ===
                                          values.yetToAddOtherContactId,
                                      )?.id ?? '',
                                    label:
                                      teamMembers.find(
                                        (item) =>
                                          item.id ===
                                          values.yetToAddOtherContactId,
                                      )?.label ?? '',
                                  }}
                                  onSelectItem={(e) => {
                                    if (e) {
                                      setTeamMembers(() => {
                                        return employeeList.map((data) => {
                                          return {
                                            label:
                                              data.lastName +
                                              ', ' +
                                              data.firstName,
                                            id: data.id,
                                          };
                                        });
                                      });
                                      setTeamMembersRole(() => {
                                        const filteredContactsRoles: OptionType[] =
                                          employeeList
                                            .filter(
                                              (contact) => contact.id === e.id,
                                            )
                                            .flatMap((contact) =>
                                              contact.roles
                                                ? contact.roles.flatMap(
                                                    (roleString) =>
                                                      roleString
                                                        .split(',')
                                                        .map((roleId) => ({
                                                          id: roleId.trim(),
                                                          label:
                                                            employeeRoles.find(
                                                              (empRole) =>
                                                                empRole.id ===
                                                                roleId.trim(),
                                                            )?.label ?? '',
                                                        })),
                                                  )
                                                : [],
                                            );
                                        return filteredContactsRoles;
                                      });
                                      setTeamMembersDiscipline(() => {
                                        const filteredContactsDisciplines =
                                          employeeList
                                            .filter(
                                              (contact) => contact.id === e.id,
                                            )
                                            .map((contact) => ({
                                              id: contact.id,
                                              label:
                                                lookups?.employeeDiscipline.find(
                                                  (dis) =>
                                                    dis.code ===
                                                    contact.disciplineType,
                                                )?.description ?? '',
                                            }));
                                        return filteredContactsDisciplines;
                                      });

                                      setFieldValue(
                                        'yetToAddOtherContactId',
                                        e.id,
                                      );
                                      setYetToAddOtherContactId(e.id);
                                    } else {
                                      setYetToAddOtherContactId('');
                                      setTeamMembersRole([]);
                                      setTeamMembersDiscipline([]);
                                      setYetToAddDisciplineId('');
                                      setRole('');
                                      setYetToAddRoleId('');
                                      formikRef.current?.setValues({
                                        ...formikRef.current?.values,
                                        yetToAddOtherContactId: '',
                                        yetToAddDisciplineId: '',
                                        yetToAddRoleId: '',
                                      });
                                    }
                                  }}
                                  Required
                                  disableLabel={false}
                                  disabled={
                                    isOtherLanguage() ||
                                    editRecordIndex !== null
                                  }
                                />
                                {errors.yetToAddOtherContactId &&
                                  touched.yetToAddOtherContactId && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.yetToAddOtherContactId}
                                      defaultlabel="Team Member Name is required"
                                    />
                                  )}
                              </Grid>
                              <Grid item xs={5} className="mb-md">
                                <DropdownComponent
                                  names={teamMembersDiscipline}
                                  labelid="Ifsp.discipline"
                                  defaultlabelid="Select Discipline"
                                  value={values.yetToAddDisciplineId}
                                  handleChange={(e) => {
                                    setFieldValue('yetToAddDisciplineId', e);
                                    setYetToAddDisciplineId(e);
                                  }}
                                  Required
                                  disabled={
                                    isOtherLanguage() || !hasEditPermission
                                  }
                                />
                                {errors.yetToAddDisciplineId &&
                                  touched.yetToAddDisciplineId && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.yetToAddDisciplineId}
                                      defaultlabel="Discipline is required"
                                    />
                                  )}
                              </Grid>
                              <Grid item xs={5} className="mb-md">
                                <DropdownComponent
                                  names={teamMembersRole}
                                  labelid="Ifsp.selectRole"
                                  defaultlabelid="Select Role"
                                  value={values.yetToAddRoleId}
                                  handleChange={(e) => {
                                    setRole(e);
                                    setFieldValue('yetToAddRoleId', e);
                                    setYetToAddRoleId(e);
                                  }}
                                  Required
                                  disabled={
                                    isOtherLanguage() || !hasEditPermission
                                  }
                                />
                                {errors.yetToAddRoleId &&
                                  touched.yetToAddRoleId && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.yetToAddRoleId}
                                      defaultlabel="Role is required"
                                    />
                                  )}
                              </Grid>
                              <Grid item xs={5}>
                                <DatePickerComponent
                                  labelid={'IFSP.startDate'}
                                  defaultlabelid={'Start Date*'}
                                  required={true}
                                  disableFuture={false}
                                  value={
                                    values.teamMemberStartDate === ''
                                      ? null
                                      : dayjs(values.teamMemberStartDate)
                                  }
                                  handlechange={(dateValue: Dayjs | null) => {
                                    if (dateValue !== null) {
                                      const formattedDate =
                                        dayjs(dateValue).format('MM/DD/YYYY');
                                      setTeamMembersStartDate(formattedDate);
                                      setFieldValue(
                                        'teamMemberStartDate',
                                        formattedDate,
                                      );
                                    } else {
                                      setTeamMembersStartDate(null);
                                      setFieldValue(
                                        'teamMemberStartDate',
                                        null,
                                      );
                                    }
                                  }}
                                  disabledDate={isOtherLanguage()}
                                />
                                {errors.teamMemberStartDate &&
                                  touched.teamMemberEndDate && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.teamMemberStartDate}
                                      defaultlabel="Start date is required"
                                    />
                                  )}
                              </Grid>
                              <Grid item xs={5}>
                                <DatePickerComponent
                                  labelid={'IFSP.teamMembersEndDate'}
                                  defaultlabelid={'End Date'}
                                  disableFuture={false}
                                  value={
                                    values.teamMemberEndDate === ''
                                      ? null
                                      : dayjs(values.teamMemberEndDate)
                                  }
                                  handlechange={(dateValue: Dayjs | null) => {
                                    if (dateValue !== null) {
                                      const formattedDate =
                                        dayjs(dateValue).format('MM/DD/YYYY');
                                      setTeamMembersEndDate(formattedDate);
                                      setFieldValue(
                                        'teamMemberEndDate',
                                        formattedDate,
                                      );
                                    } else {
                                      setTeamMembersEndDate(null);
                                      setFieldValue('teamMemberEndDate', null);
                                    }
                                  }}
                                  disabledDate={isOtherLanguage()}
                                />
                                {errors.teamMemberEndDate &&
                                  touched.teamMemberEndDate && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.teamMemberEndDate}
                                    />
                                  )}
                              </Grid>
                              {isOtherLanguage() ? null : (
                                <Grid
                                  container
                                  xs={1}
                                  className="cursorPointer "
                                  onClick={() => {
                                    handleSubmit();
                                  }}
                                >
                                  <Grid item>
                                    <Box
                                      component="div"
                                      className="flex__ pt-xs "
                                    >
                                      <AddNew className="ml-sm mr-sm" />
                                      <MediumTypography
                                        labelid={
                                          editRecordIndex !== null
                                            ? 'IFSP.Update'
                                            : 'IFSP.Add'
                                        }
                                        defaultlabel={
                                          editRecordIndex !== null
                                            ? 'Update'
                                            : 'Add'
                                        }
                                      />
                                    </Box>
                                  </Grid>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        )}
                        {contactType === 'other' && (
                          <Box
                            component="div"
                            className="mt-md mb-md"
                            sx={{
                              pointerEvents: hasEditPermission
                                ? 'auto'
                                : 'none',
                              opacity: hasEditPermission ? 'unset' : '0.8',
                            }}
                          >
                            <Grid container columnSpacing={'32px'}>
                              <Grid item xs={5} className="mb-md">
                                <Textinput
                                  labelid="IFSP.Name"
                                  defaultlabelid="Name"
                                  Value={values.othersName}
                                  inputProps={{
                                    className: 'translate',
                                    maxLength: 40,
                                  }}
                                  handlechange={(e) => {
                                    setFieldValue('othersName', e);
                                    setOthersName(e);
                                  }}
                                  Required
                                />
                                {errors.othersName && touched.othersName && (
                                  <MediumTypography
                                    className="errorText-md"
                                    labelid={errors.othersName}
                                    defaultlabel="Name is required"
                                  />
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={5}
                                className="mb-md"
                                display={'none'}
                              >
                                <Textinput
                                  labelid="IFSP.JobDescription"
                                  defaultlabelid="Job Description"
                                  Value={values.othersJobDescription}
                                  inputProps={{
                                    className: 'translate',
                                    maxLength: 200,
                                  }}
                                  handlechange={(e) => {
                                    setFieldValue('othersJobDescription', e);
                                    setOthersJobDescription(e);
                                  }}
                                  Required
                                />
                                {errors.othersJobDescription &&
                                  touched.othersJobDescription && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.othersJobDescription}
                                      defaultlabel="Job Description is required"
                                    />
                                  )}
                              </Grid>
                              <Grid item xs={5}>
                                <DatePickerComponent
                                  labelid={'IFSP.startDate'}
                                  defaultlabelid={'Start Date*'}
                                  required={true}
                                  disableFuture={false}
                                  value={
                                    values.othersStartDate === ''
                                      ? null
                                      : dayjs(values.othersStartDate)
                                  }
                                  handlechange={(dateValue: Dayjs | null) => {
                                    if (dateValue !== null) {
                                      const formattedDate =
                                        dayjs(dateValue).format('MM/DD/YYYY');
                                      setOthersStartDate(formattedDate);
                                      setFieldValue(
                                        'othersStartDate',
                                        formattedDate,
                                      );
                                    } else {
                                      setOthersStartDate(null);
                                      setFieldValue('othersStartDate', null);
                                    }
                                  }}
                                  disabledDate={isOtherLanguage()}
                                />
                                {errors.othersStartDate &&
                                  touched.othersStartDate && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.othersStartDate}
                                      defaultlabel="Start date is required"
                                    />
                                  )}
                              </Grid>
                              <Grid item xs={5}>
                                <DatePickerComponent
                                  labelid={'IFSP.teamMembersEndDate'}
                                  defaultlabelid={'End Date'}
                                  disableFuture={false}
                                  value={
                                    values.othersEndDate === ''
                                      ? null
                                      : dayjs(values.othersEndDate)
                                  }
                                  handlechange={(dateValue: Dayjs | null) => {
                                    if (dateValue !== null) {
                                      const formattedDate =
                                        dayjs(dateValue).format('MM/DD/YYYY');
                                      setOthersEndDate(formattedDate);
                                      setFieldValue(
                                        'othersEndDate',
                                        formattedDate,
                                      );
                                    } else {
                                      setOthersEndDate(null);
                                      setFieldValue('othersEndDate', null);
                                    }
                                  }}
                                  disabledDate={isOtherLanguage()}
                                />
                                {errors.othersEndDate &&
                                  touched.othersEndDate && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.othersEndDate}
                                      defaultlabel="End date is required"
                                    />
                                  )}
                              </Grid>
                              {isOtherLanguage() ? null : (
                                <Grid
                                  container
                                  xs={1}
                                  className="cursorPointer "
                                  onClick={() => {
                                    handleSubmit();
                                  }}
                                >
                                  <Grid item>
                                    <Box
                                      component="div"
                                      className="flex__ pt-xs "
                                    >
                                      <AddNew className="ml-sm mr-sm" />
                                      <MediumTypography
                                        labelid={
                                          editOtherRecordIndex !== null
                                            ? 'IFSP.Update'
                                            : 'IFSP.Add'
                                        }
                                        defaultlabel={
                                          editOtherRecordIndex !== null
                                            ? 'Update'
                                            : 'Add'
                                        }
                                      />
                                    </Box>
                                  </Grid>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        )}
                        <Box
                          component="div"
                          className="respoScrollTable mt-lg mb-md"
                          sx={{
                            pointerEvents: hasEditPermission ? 'auto' : 'none',
                            opacity: hasEditPermission ? 'unset' : '0.8',
                          }}
                        >
                          <TableContainer component={Paper}>
                            <Table>
                              <TableHeader
                                className="listDataTableHead"
                                headerNames={headCells}
                                checkBoxRequired={false}
                              />
                              <TableBody className="tableRowcss">
                                {ifspTeamTable.map((listData, index) => (
                                  <IfspTeamMembersTableRow
                                    isIfspScreenLocked={isOtherLanguage()}
                                    key={`team-member-${index + 1}`}
                                    data={listData}
                                    onDelete={() =>
                                      handleDeleteOtherContact(index)
                                    }
                                    onEdit={() => {
                                      if (listData.role) {
                                        handleEditTeamMemberContact(index);
                                      } else {
                                        handleEditOtherContact(index);
                                      }
                                    }}
                                  />
                                ))}
                              </TableBody>
                            </Table>
                            {ifspTeamTable.length === 0 && (
                              <Box component="div" className="width__100">
                                <MediumTypography
                                  textstyle={'center'}
                                  labelid="IFSP.emptyMessage"
                                  defaultlabel="No data added yet"
                                  className="p-sm"
                                />
                              </Box>
                            )}
                          </TableContainer>
                        </Box>
                        <Box className="horizontalLine" />
                        <Box
                          ref={autoScrollRef}
                          sx={{
                            pointerEvents: hasEditPermission ? 'auto' : 'none',
                            opacity: hasEditPermission ? 'unset' : '0.8',
                          }}
                        >
                          {isFromClientJourny && (
                            <Box component="div" className="mt-lg mb-lg">
                              <TransitionPlan
                                key={refresh}
                                selectedLanguage={selectedLang}
                                info={infoTransitionPlanModal}
                                open={openTransitionModal}
                                onOpen={() => {
                                  setOpenTransitionModal(true);
                                }}
                                openInfo={() => {
                                  setInfoTransitionPlanModal(true);
                                }}
                                onClose={() => {
                                  setOpenTransitionModal(false);
                                }}
                                closeInfo={() => {
                                  setInfoTransitionPlanModal(false);
                                }}
                                hasEditPermission={hasEditPermission}
                              />
                            </Box>
                          )}
                        </Box>
                        <MediumTypography
                          labelid="IFSP.Report"
                          defaultlabel="Report"
                          fontSize="18px"
                          fontweight={600}
                          className="pb-md mt-lg"
                        />
                        <Box
                          className="borderRadius4"
                          sx={{
                            backgroundColor: '#E7F0F0',
                          }}
                        >
                          <Box component="div" className="pt-lg mb-lg">
                            <DailyRoutine
                              key={refresh}
                              info={infoDailyRoutineModal}
                              close={closeDailyRoutine}
                              handelClose={setCloseDailyRoutine}
                              selectedLanguage={selectedLang}
                              openInfo={() => {
                                setInfoDailyRoutineModal(true);
                              }}
                              closeInfo={() => {
                                setInfoDailyRoutineModal(false);
                              }}
                              hasEditPermission={hasEditPermission}
                            />
                          </Box>
                          <Box component="div">
                            <FamiliyConcernsPrioritiesandResources
                              key={refresh}
                              close={closeFamilyConcern}
                              handelClose={setCloseFamilyConcern}
                              info={infoFamilyConcernsModal}
                              selectedLanguage={selectedLang}
                              openInfo={() => {
                                setInfoFamilyConcernsModal(true);
                              }}
                              closeInfo={() => {
                                setInfoFamilyConcernsModal(false);
                              }}
                              hasEditPermission={hasEditPermission}
                            />
                          </Box>
                          <Box component="div">
                            <HealthEligibilitySummary
                              key={refresh}
                              close={closeHealthEligibility}
                              handelClose={setCloseHealthEligibility}
                              info={infoHESModal}
                              selectedLanguage={selectedLang}
                              openInfo={() => {
                                setInfoHESModal(true);
                              }}
                              closeInfo={() => {
                                setInfoHESModal(false);
                              }}
                              hasEditPermission={hasEditPermission}
                            />
                          </Box>
                          <Box component="div">
                            <DevelopmentalSummary
                              key={refresh}
                              close={closeDevelopmentSummary}
                              handelClose={setCloseDevelopmentSummary}
                              info={infoDevelopmentalSummaryModal}
                              selectedLanguage={selectedLang}
                              openInfo={() => {
                                setInfoDevelopmentalSummaryModal(true);
                              }}
                              closeInfo={() => {
                                setInfoDevelopmentalSummaryModal(false);
                              }}
                              hasEditPermission={hasEditPermission}
                            />
                          </Box>
                          <Box className="p-lg">
                            <Grid
                              container
                              direction={'row'}
                              sx={{
                                borderTop: '1px solid #00C6B8',
                                pointerEvents: hasEditPermission
                                  ? 'auto'
                                  : 'none',
                                opacity: hasEditPermission ? 'unset' : '0.8',
                              }}
                            >
                              <Grid item xs={5.2} className="mt-lg">
                                <AutocompleteSearch
                                  className="bg__white"
                                  labelId={'IFSP.reportCompletedBy'}
                                  defaultId="Report Completed By"
                                  optionHintId={'noemployeeDropdownTextId'}
                                  sxProps={{
                                    [`& .${autocompleteClasses.popupIndicator}`]:
                                      {
                                        transform: 'none',
                                      },
                                  }}
                                  data={employeeList.map((item) => ({
                                    id: item.id,
                                    label:
                                      item.firstName !== null
                                        ? item.lastName + ', ' + item.firstName
                                        : '',
                                  }))}
                                  onSelectItem={(value: OptionType | null) => {
                                    if (value) {
                                      setIsUserManuallyAddedReportedBy(true);
                                      setReportedCompletedBy(value);
                                      updateIfspContext({
                                        ...ifsp,
                                        completedBy: {
                                          id: value.id,
                                          label: value.label,
                                        },
                                      });
                                      setReportCompletedByRole(() => {
                                        const filteredContactsRoles: OptionType[] =
                                          employeeList
                                            .filter(
                                              (contact) =>
                                                contact.id === value.id,
                                            )
                                            .flatMap((contact) =>
                                              contact.roles
                                                ? contact.roles.flatMap(
                                                    (roleString) =>
                                                      roleString
                                                        .split(',')
                                                        .map((roleId) => ({
                                                          id: roleId.trim(),
                                                          label:
                                                            employeeRoles.find(
                                                              (empRole) =>
                                                                empRole.id ===
                                                                roleId.trim(),
                                                            )?.label ?? '',
                                                        })),
                                                  )
                                                : [],
                                            );
                                        return filteredContactsRoles;
                                      });
                                      setIsreportCompletedByMandatory(false);
                                      setReportCompletedByRoleId('');
                                      setIsreportCompletedRoleMandatory(true);
                                    } else {
                                      setIsUserManuallyAddedReportedBy(false);
                                      setReportedCompletedBy({
                                        id: '',
                                        label: '',
                                      });
                                      updateIfspContext({
                                        ...ifsp,
                                        completedByRole: '',
                                        completedBy: {
                                          id: '',
                                          label: '',
                                        },
                                      });
                                      setReportCompletedByRole([]);
                                      if (
                                        isDailyRoutineUpdated ||
                                        isDevelopmentSummaryUpdated ||
                                        isFamilyConcernsUpdated ||
                                        isHealthAndEligibilityUpdated
                                      ) {
                                        setIsreportCompletedByMandatory(true);
                                        setIsreportCompletedRoleMandatory(true);
                                      } else {
                                        setIsreportCompletedByMandatory(false);
                                      }
                                      setIsreportCompletedRoleMandatory(false);
                                      setReportCompletedByRoleId('');
                                    }
                                  }}
                                  selectedValue={{
                                    id: reportedCompletedBy?.id || '',
                                    label:
                                      formatName(
                                        employeeList.find(
                                          (emp) =>
                                            emp.id === reportedCompletedBy?.id,
                                        )?.firstName ?? '',
                                        null,
                                        employeeList.find(
                                          (emp) =>
                                            emp.id === reportedCompletedBy?.id,
                                        )?.lastName ?? '',
                                        null,
                                      ) || '',
                                    // label: reportedCompletedBy?.label || '',
                                  }}
                                  disableLabel={false}
                                  disabled={isOtherLanguage()}
                                />
                                {isreportCompletedByMandatory && (
                                  <MediumTypography
                                    className="errorText-md"
                                    labelid={'IFSP.reportCompletedByIsRequired'}
                                    defaultlabel="Report Completed By is required"
                                  />
                                )}
                              </Grid>
                              <Grid item xs={0.5} className="mt-lg"></Grid>
                              <Grid item xs={5.2} className="mt-lg">
                                <DropdownComponent
                                  names={reportCompletedByRole}
                                  labelid="IFSP.selectRoleNonMandatory"
                                  defaultlabelid="Select Role"
                                  value={reportCompletedByRoleId}
                                  handleChange={(e) => {
                                    updateIfspContext({
                                      ...ifsp,
                                      completedByRole: e,
                                    });
                                    setReportCompletedByRoleId(e);
                                    setIsreportCompletedRoleMandatory(!e);
                                  }}
                                  disabled={
                                    isOtherLanguage() || !hasEditPermission
                                  }
                                />
                                {isreportCompletedRoleMandatory && (
                                  <MediumTypography
                                    className="errorText-md"
                                    labelid={'IFSP.roleIsRequired'}
                                    defaultlabel="Role is required"
                                  />
                                )}
                              </Grid>
                            </Grid>
                          </Box>
                        </Box>
                        <Box
                          component="div"
                          className="flex__ align__items__center justifyContentSpaceBtw pb-md mt-lg"
                        >
                          <Box className="flex__ align__items__center">
                            <MediumTypography
                              labelid="IFSP.ServiceDelivery"
                              defaultlabel="Outcomes and Service Delivery"
                              fontSize="18px"
                              fontweight={600}
                              className="mr-md"
                            />
                          </Box>
                        </Box>
                        <Box
                          className="borderRadius4 pb-lg"
                          sx={{
                            backgroundColor: '#E7F0F0',
                          }}
                        >
                          <Box className="flex__ justifyContentSpaceBtw">
                            <Box></Box>
                            <Box
                              className={
                                ifspScreenFieldLock || ifsp.isLocked
                                  ? 'pt-lg'
                                  : ''
                              }
                            >
                              {(ifspScreenFieldLock || ifsp.isLocked) &&
                                ifspScreenLockedToaster()}
                            </Box>
                            <Box
                              className={
                                (ifspScreenFieldLock || ifsp.isLocked) &&
                                !isOtherLanguage() &&
                                hasEditPermission
                                  ? 'pt-lg mr-lg'
                                  : ''
                              }
                            >
                              {(ifspScreenFieldLock || ifsp.isLocked) &&
                                !isOtherLanguage() &&
                                hasEditPermission && (
                                  <ButtonComponent
                                    className="btn-primary btn-submit ml-md"
                                    labelId="IFSP.Update"
                                    defaultLabelId="Update"
                                    variantType="contained"
                                    onClick={() => {
                                      setOpenUpdateModal(true);
                                    }}
                                  />
                                )}
                            </Box>
                          </Box>
                          <Box component="div" className="mb-lg pt-lg">
                            <Box
                              className="ml-lg mr-lg pb-lg"
                              sx={{
                                borderBottom: '1px solid #00C6B8',
                                pointerEvents: hasEditPermission
                                  ? 'auto'
                                  : 'none',
                                opacity: hasEditPermission ? 'unset' : '0.8',
                              }}
                            >
                              <Grid container>
                                <Grid container rowSpacing={'24px'}>
                                  <Grid item xs={5.2}>
                                    <DatePickerComponent
                                      className="bg__white"
                                      labelid={'IFSP.FirstMeetingDate'}
                                      defaultlabelid={'IFSP First Meeting Date'}
                                      disableFuture={false}
                                      value={dayjs(ifsp.ifspMeetingDate)}
                                      handlechange={(
                                        dateValue: Dayjs | null,
                                      ) => {
                                        if (dateValue !== null) {
                                          const formattedDate =
                                            dayjs(dateValue).format(
                                              'MM/DD/YYYY',
                                            );
                                          updateIfspContext({
                                            ...ifsp,
                                            ifspMeetingDate: formattedDate,
                                          });
                                        } else {
                                          updateIfspContext({
                                            ...ifsp,
                                            ifspMeetingDate: null,
                                          });
                                        }
                                      }}
                                      required
                                      disabledDate={
                                        (isOtherLanguage() ||
                                          !!ifsp.ifspTeam.forms
                                            .consentForChangeForSDP[0]
                                            .sessionReviewedDate ||
                                          !hasEditPermission) &&
                                        !canEditAfterLock
                                      }
                                      maxDate={dayjs()}
                                    />
                                  </Grid>
                                  <Grid item xs={0.5}></Grid>
                                  <Grid item xs={5.2}>
                                    <DatePickerComponent
                                      className="bg__white"
                                      labelid={'IFSP.SignedDate'}
                                      defaultlabelid={'IFSP Signed Date'}
                                      disableFuture={false}
                                      value={dayjs(ifsp.ifspSignedDate)}
                                      handlechange={(
                                        dateValue: Dayjs | null,
                                      ) => {
                                        if (dateValue !== null) {
                                          const formattedDate =
                                            dayjs(dateValue).format(
                                              'MM/DD/YYYY',
                                            );
                                          updateIfspContext({
                                            ...ifsp,
                                            ifspSignedDate: formattedDate,
                                          });
                                        } else {
                                          updateIfspContext({
                                            ...ifsp,
                                            ifspSignedDate: null,
                                          });
                                        }
                                      }}
                                      required
                                      disabledDate={
                                        (isOtherLanguage() ||
                                          !!ifsp.ifspTeam.forms
                                            .consentForChangeForSDP[0]
                                            .sessionReviewedDate ||
                                          !hasEditPermission) &&
                                        !canEditAfterLock
                                      }
                                      minDate={dayjs(ifsp.ifspMeetingDate)}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Box>
                            <ChildandFamilyIFSPOutcomes
                              key={refresh}
                              isIfspScreenLocked={
                                ifspScreenFieldLock ||
                                ifsp.isLocked ||
                                selectedLang !== 'en' ||
                                !hasEditPermission
                              }
                              canEditAfterLock={canEditAfterLock}
                              canDeleteAfterLock={canDeleteAfterLock}
                              info={infoOutComeModal}
                              open={openOutCome}
                              onOpen={() => {
                                setOpenOutCome(true);
                              }}
                              openInfo={() => {
                                setInfoOutComeModal(true);
                              }}
                              onClose={() => {
                                setOpenOutCome(false);
                              }}
                              closeInfo={() => {
                                setInfoOutComeModal(false);
                              }}
                              reviewInfo={infoReviewModal}
                              openReviewInfo={() => {
                                setInfoReviewModal(true);
                              }}
                              closeReviewInfo={() => {
                                setInfoReviewModal(false);
                              }}
                              isErrorOccured={(error) => {
                                setIsErrorOccuredInOutCome(error);
                              }}
                              isErrorOccuredInOutComeEndDate={(error) => {
                                setIsErrorOccuredInOutComeEndDate(error);
                              }}
                              isErrorOccuredInOutComeStartDate={(error) => {
                                setIsErrorOccuredInOutComeStartDate(error);
                              }}
                              hasEditPermission={hasEditPermission}
                            />
                          </Box>
                          <Box component="div" className="mt-lg mb-lg">
                            <ServiceDeliveryPlan
                              hasEditPermission={hasEditPermission}
                              key={refresh}
                              isIfspScreenLocked={
                                (ifspScreenFieldLock && canEditAfterLock) ||
                                ifsp.isLocked ||
                                selectedLang !== 'en' ||
                                !hasEditPermission
                              }
                              canEditAfterLock={canEditAfterLock}
                              canDeleteAfterLock={canDeleteAfterLock}
                              info={infoSDPModal}
                              open={openSDP}
                              isSavedClicked={saveClicked}
                              onOpen={() => {
                                setOpenSDP(true);
                              }}
                              openInfo={() => {
                                setInfoSDPModal(true);
                              }}
                              onClose={() => {
                                setOpenSDP(false);
                              }}
                              closeInfo={() => {
                                setInfoSDPModal(false);
                              }}
                            />
                          </Box>
                          <Box component="div" className="mt-lg">
                            <ConsentChangeServiceDeliveryPlan
                              key={refresh}
                              isIfspScreenLocked={
                                ifspScreenFieldLock || ifsp.isLocked
                              }
                              canEditAfterLock={canEditAfterLock}
                              selectedLanguage={selectedLang}
                              close={closeConsentChangeSDP}
                              handelClose={setCloseConsentChangeSDP}
                              info={infoConsentChangeSDPModal}
                              openInfo={() => {
                                setInfoConsentChangeSDPModal(true);
                              }}
                              closeInfo={() => {
                                setInfoConsentChangeSDPModal(false);
                              }}
                              hasEditPermission={hasEditPermission}
                            />
                          </Box>
                        </Box>
                        <MediumTypography
                          labelid="IFSP.Transition"
                          defaultlabel="Transition"
                          fontSize="18px"
                          fontweight={600}
                          className="pb-md mt-lg"
                        />
                        <Box
                          className="borderRadius4"
                          sx={{
                            backgroundColor: '#E7F0F0',
                          }}
                        >
                          <Box component="div" className="pt-lg mb-lg">
                            <TransitionPreparation
                              key={refresh}
                              selectedLanguage={selectedLang}
                              info={infoTransistionPrepModal}
                              open={openTransistionPrep}
                              onOpen={() => {
                                setOpenTransistionPrep(true);
                              }}
                              openInfo={() => {
                                setInfoTransistionPrepModal(true);
                              }}
                              onClose={() => {
                                setOpenTransistionPrep(false);
                              }}
                              closeInfo={() => {
                                setInfoTransistionPrepModal(false);
                              }}
                              hasEditPermission={hasEditPermission}
                            />
                          </Box>

                          {!isFromClientJourny && (
                            <Box component="div" className="mt-lg pb-lg">
                              <TransitionPlan
                                key={refresh}
                                selectedLanguage={selectedLang}
                                info={infoTransitionPlanModal}
                                open={openTransitionModal}
                                onOpen={() => {
                                  setOpenTransitionModal(true);
                                }}
                                openInfo={() => {
                                  setInfoTransitionPlanModal(true);
                                }}
                                onClose={() => {
                                  setOpenTransitionModal(false);
                                }}
                                closeInfo={() => {
                                  setInfoTransitionPlanModal(false);
                                }}
                                hasEditPermission={hasEditPermission}
                              />
                            </Box>
                          )}
                        </Box>
                      </Card>

                      <Box
                        component="div"
                        className="flex__ justifyContent-FlexEnd mt-md mb-xlg"
                      >
                        <ButtonComponent
                          className="btn-primary btn-cancel"
                          labelId={'cancelText'}
                          defaultLabelId="Cancel"
                          variantType="outlined"
                          sxProps={{
                            display: hasEditPermission ? 'auto' : 'none',
                          }}
                          onClick={() => {
                            if (!ifspTouched) {
                              const clientId = localStorage.getItem('ClientId');
                              if (!isIfspInitiated || ifsp.id) {
                                navigation(FACESHEET, { state: { clientId } });
                              } else {
                                setIsIfspInitiated(false);
                              }
                            } else {
                              setOpenModal(true);
                            }
                          }}
                        />
                        <ButtonComponent
                          className="btn-primary btn-submit  ml-md"
                          labelId="ConsentSave"
                          defaultLabelId="Save"
                          variantType="contained"
                          sxProps={{
                            display: hasEditPermission ? 'auto' : 'none',
                          }}
                          onClick={() => {
                            setOpenTransitionModal(false);
                            setCloseDailyRoutine(true);
                            setCloseHealthEligibility(true);
                            setCloseFamilyConcern(true);
                            setCloseDevelopmentSummary(true);
                            setCloseConsentChangeSDP(true);
                            setOpenSDP(false);
                            setOpenOutCome(false);
                            setOpenTransistionPrep(false);
                            if (
                              !ifsp.primarySettingCode &&
                              isLatestConsentSigned
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                "Primary Setting can't be empty",
                              );
                              setToastrId('IFSP.primarySettingRequired');
                            } else if (
                              (ifsp.ifspMeetingDate === '' ||
                                ifsp.ifspMeetingDate === null) &&
                              isLatestConsentSigned
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                "IFSP Meeting Date can't be empty",
                              );
                              setToastrId('IFSP.MeetingDateRequired');
                            } else if (
                              ifsp.ifspMeetingDate &&
                              dayjs(ifsp.ifspMeetingDate, 'MM/DD/YYYY').isAfter(
                                dayjs(),
                                'day',
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                "IFSP Meeting Date can't be future date",
                              );
                              setToastrId('IFSP.MeetingDateFutureDate');
                            } else if (
                              ifsp.ifspMeetingDate &&
                              dayjs(ifsp.ifspMeetingDate).isBefore(
                                dayjs(ifsp.initialEvalNotificationDate),
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                "IFSP Meeting Date can't be before Initial Evaluation Notification Date",
                              );
                              setToastrId(
                                'IFSP.MeetingDateBeforeInitialEvalNotificationDate',
                              );
                            } else if (
                              ifsp.ifspMeetingDate &&
                              dayjs(ifsp.ifspMeetingDate).isAfter(
                                dayjs(ifsp.client.dob).add(3, 'year'),
                                'day',
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                "IFSP Meeting Date can't be after the 3rd birthday",
                              );
                              setToastrId('IFSP.MeetingDateAfter3rdBirthDay');
                            } else if (
                              datePickerMinDateMaxDateValidate(
                                ifsp.ifspMeetingDate,
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Meeting Date is invalid',
                              );
                              setToastrId('datePickerMinDateMaxDateValidate');
                            } else if (
                              (ifsp.ifspSignedDate === '' ||
                                ifsp.ifspSignedDate === null) &&
                              isLatestConsentSigned
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                "IFSP Signed Date can't be empty",
                              );
                              setToastrId('IFSP.ifspSignedDateRequired');
                            } else if (
                              ifsp.ifspSignedDate &&
                              dayjs(ifsp.ifspSignedDate).isBefore(
                                ifsp.ifspMeetingDate,
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                "IFSP Signed Date can't be before IFSP Meeting Date",
                              );
                              setToastrId(
                                'IFSP.ifspSignedDateBeforeIfspMeetingDate',
                              );
                            } else if (
                              datePickerMinDateMaxDateValidate(
                                ifsp.ifspSignedDate,
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'IFSP Signed Date is invalid',
                              );
                              setToastrId('datePickerMinDateMaxDateValidate');
                            } else if (
                              ifsp.primarySettingCode != 'HOME' &&
                              ifsp.primarySettingCode != 'COMMUNITY' &&
                              ifsp.primarySettingCode &&
                              (ifsp.primarySettingCodeJustification === '' ||
                                ifsp.primarySettingCodeJustification === null)
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Justification is required',
                              );
                              setToastrId(
                                'IFSP.primarySettingCodeJustificationRequired',
                              );
                            } else if (
                              isErrorOccuredInOutComeStartDate &&
                              ifsp.ifspMeetingDate
                            ) {
                              updateOutComesStartDateFlag(true);
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'One or more outcomes have an Start Date that occurs before the Meeting Date. Update the dates to proceed',
                              );
                              setToastrId('IFSP.outcomeEndDateBeforeStartDate');
                            } else if (isErrorOccuredInOutComeEndDate) {
                              updateOutComesEndDateFlag(true);
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'One or more outcomes have an End Date that occurs before the Start Date. Update the dates to proceed',
                              );
                              setToastrId('IFSP.outcomeEndDateBeforeStartDate');
                            } else if (
                              isErrorOccuredInOutCome &&
                              isLatestConsentSigned
                            ) {
                              updateOutComesFieldFlag(true);
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Some of the Outcomes are partially completed. Complete all Outcomes to save IFSP',
                              );
                              setToastrId('IFSP.outcomeRequired');
                            } else if (
                              datePickerMinDateMaxDateValidate(
                                latestConsentForChange.assessmentCompletionDate,
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Assessment Date is invalid',
                              );
                              setToastrId('datePickerMinDateMaxDateValidate');
                            } else if (
                              ifsp.ifspTeam.forms.consentForChangeForSDP.some(
                                (consent) => {
                                  return (
                                    consent.consentType === 'consent' &&
                                    consent.isEligibleUntilThirdBirthday ===
                                      'iseligible' &&
                                    (consent.reEstablishedDate === null ||
                                      consent.reEstablishedDate === '')
                                  );
                                },
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Re-Established Date is required',
                              );
                              setToastrId('IFSP.reEstablishedDateRequired');
                            } else if (
                              datePickerMinDateMaxDateValidate(
                                latestConsentForChange.reEstablishedDate,
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Re-Established Date is invalid',
                              );
                              setToastrId('datePickerMinDateMaxDateValidate');
                            } else if (
                              datePickerMinDateMaxDateValidate(
                                ifsp.ifspTeam.forms.transitionPlan.consentDate,
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Consent Date is invalid',
                              );
                              setToastrId('datePickerMinDateMaxDateValidate');
                            } else if (
                              datePickerMinDateMaxDateValidate(
                                ifsp.ifspTeam.forms.transitionPlan.meetingDate,
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Transition Meeting Date is invalid',
                              );
                              setToastrId('datePickerMinDateMaxDateValidate');
                            } else if (
                              datePickerMinDateMaxDateValidate(
                                ifsp.ifspTeam.forms.transitionPlan
                                  .leaOptOutDate,
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Lea Opt Out Date is invalid',
                              );
                              setToastrId('datePickerMinDateMaxDateValidate');
                            } else if (
                              datePickerMinDateMaxDateValidate(
                                ifsp.ifspTeam.forms.transitionPreparation
                                  .transitionPreparationDate,
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Transition Preparation Date is invalid',
                              );
                              setToastrId('datePickerMinDateMaxDateValidate');
                            } else if (
                              datePickerMinDateMaxDateValidate(
                                ifsp.ifspTeam.forms.transitionPlan
                                  .transitionPlanDate,
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Transition Plan Date is invalid',
                              );
                              setToastrId('datePickerMinDateMaxDateValidate');
                            } else if (
                              datePickerMinDateMaxDateValidate(
                                ifsp.ifspTeam.forms.transitionPlan.date,
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage('Date is invalid');
                              setToastrId('datePickerMinDateMaxDateValidate');
                            } else if (
                              datePickerMinDateMaxDateValidate(
                                ifsp.ifspTeam.forms.healthSummary[
                                  ifsp.ifspTeam.forms.healthSummary.length - 1
                                ].dateOfEvaluation,
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Date of Evaluation is invalid',
                              );
                              setToastrId('datePickerMinDateMaxDateValidate');
                            } else if (
                              (isDailyRoutineUpdated ||
                                isDevelopmentSummaryUpdated ||
                                isHealthAndEligibilityUpdated ||
                                isFamilyConcernsUpdated) &&
                              (!ifsp.completedBy || !ifsp.completedByRole)
                            ) {
                              const missingFields = [
                                !ifsp.completedBy?.id,
                                !ifsp.completedByRole,
                              ];

                              const missingFieldHandlers = [
                                () => setIsreportCompletedByMandatory(true),
                                () => setIsreportCompletedRoleMandatory(true),
                              ];

                              missingFields.forEach((missing, index) => {
                                if (missing) missingFieldHandlers[index]();
                              });
                            } else if (
                              (!hasChildOutcomes || !hasServiceDeliveryPlan) &&
                              isLatestConsentSigned
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Missing child outcomes or service delivery plan',
                              );
                              setToastrId(
                                'IFSP.missingChildOutcomesOrServiceDeliveryPlan',
                              );
                            } else if (
                              ifsp.ifspTeam.forms.consentForChangeForSDP.some(
                                (consentForChange) =>
                                  consentForChange.consentSignature.some(
                                    (cs) =>
                                      !cs.date &&
                                      (cs.signature ||
                                        cs.rowKey ||
                                        cs.verbalConsent),
                                  ) ||
                                  consentForChange.parentSignature.some(
                                    (ps) =>
                                      !ps.date &&
                                      (ps.signature ||
                                        ps.rowKey ||
                                        ps.verbalConsent),
                                  ),
                              ) ||
                              ifsp.ifspTeam.forms.consentForChangeForSDP.some(
                                (consentForChange) =>
                                  consentForChange.employeeSignature
                                    ?.signature &&
                                  !consentForChange.employeeSignature?.date,
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Date of the Consent or Consent for Change cannot be empty',
                              );
                              setToastrId('IFSP.missingSignatureDate');
                            } else if (
                              ifsp.ifspTeam.forms.consentForChangeForSDP.some(
                                (consentChange) =>
                                  consentChange.isConsentSkipped &&
                                  consentChange.isAdminAmendment &&
                                  (!consentChange.changes ||
                                    !consentChange.employeeSignature.signature),
                              )
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Some of the Admin Amendments are partially completed. Complete all the Admin Amendments to save IFSP',
                              );
                              setToastrId('IFSP.missingAdminAmendments');
                            } else if (
                              dayjs(
                                ifsp.ifspTeam.forms.transitionPlan.date,
                              ).isBefore(dayjs(ifsp.referralBeginDate))
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                "Referral to LEA can't be before the Referral begin date",
                              );
                              setToastrId(
                                'IFSP.transitionPlanDateBeforeReferralBeginDate',
                              );
                            } else if (
                              transitionDateOutOfRange &&
                              ifsp.ifspTeam.forms.transitionPlan
                                .noTransitionPlanReason === ''
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Reason for “No transition plan” or outside “expected” timeframe',
                              );
                              setToastrId(
                                'IFSP.NoTransitionPlanReasonRequired',
                              );
                            } else if (
                              leaReferralDateOutOfRange &&
                              ifsp.ifspTeam.forms.transitionPlan
                                .outsideTPCReason === ''
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Reason if LEA referral is outside of standard timeframe (27-33 mo) is required',
                              );
                              setToastrId('IFSP.LEAReferralReasonRequired');
                            } else if (
                              tpcMeetingDateOutOfRange &&
                              ifsp.ifspTeam.forms.transitionPlan.noTPCReason ===
                                ''
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Reason for "No TC" or outside "expected" time frame is required',
                              );
                              setToastrId('IFSP.NoReasonTpcOccuredIsRequired');
                            } else if (isOptOutNotificationDateRequired) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'RLEA Opt-out notification date is required.',
                              );
                              setToastrId('IFSP.LeaOptoutDateRequired');
                            } else if (isTransitionDateReqired) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Date of Transition Plan is required',
                              );
                              setToastrId(
                                'IFSP.DateOfTransitionPlanIsRequired',
                              );
                            } else if (isLeaReferralDateRequired) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Consent Date is required',
                              );
                              setToastrId(
                                ' IFSP.DateOfTransitionPlanIsRequired',
                              );
                            } else if (isTpcMeetingDateRequired) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'Meeting Date is required',
                              );
                              setToastrId(
                                ' IFSP.DateOfTransitionPlanIsRequired',
                              );
                            } else if (
                              ifsp.ifspTeam.forms.transitionPlan.date &&
                              ifsp.dischargeDate &&
                              dayjs(
                                ifsp.ifspTeam.forms.transitionPlan.date,
                              ).isAfter(dayjs(ifsp.dischargeDate))
                            ) {
                              setOpen(true);
                              setToastrVariable('error');
                              setToastrDefaultMessage(
                                'The Referral Date must be on or before the Discharge Date.',
                              );
                              setToastrId(
                                'IFSP.transitionPlanDateBeforeDischargeDate',
                              );
                            } else if (
                              dayjs(ifsp.ifspMeetingDate).isAfter(
                                dayjs(ifsp.ifspDueDate),
                              ) &&
                              ifsp.reasonForDelay.id === ''
                            ) {
                              setOpenDelayModal(true);
                            } else {
                              handleCreateIFSP();
                            }
                          }}
                          disabled={
                            isreportCompletedByMandatory ||
                            isreportCompletedRoleMandatory
                          }
                        />
                      </Box>
                    </Box>
                  )}
                </Formik>
              </Box>
            )}
          </Box>
          {shareModal && (
            <ShareDownloadModal
              modalVisible={shareModal}
              closeModal={() => setShareModal(false)}
              screenName="IFSP"
            />
          )}
        </TranslateWidget>
      )}
    </Box>
  );
};
export default IFSPScreen;
